import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Divider,
  Stack,
  Tooltip,
  Badge,
  TablePagination,
  Collapse,
  Checkbox,
  LinearProgress,
  FormControlLabel,
  Avatar,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Visibility,
  Edit,
  CheckCircle,
  Cancel,
  Refresh,
  Search,
  FilterList,
  KeyboardArrowDown,
  KeyboardArrowUp,
  SwapHoriz,
  Undo,
  LocalShipping,
  AccountBalance,
  Phone,
  Email,
  ShoppingCart,
  Receipt,
  Notes,
  Store,
  AddShoppingCart,
  Storefront,
  EventBusy,
  Inventory2,
  Replay
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';

interface ExchangeReturnRequest {
  id: number;
  request_id: string;
  original_order_number: string;
  customer_email: string;
  customer_name: string;
  customer_phone: string;
  request_type: 'exchange' | 'return';
  status: 'pending' | 'approved' | 'processing' | 'completed' | 'cancelled';

  // Mixed submission linkage: when a customer files a return + exchange in ONE
  // submission, the backend creates two rows sharing this group id.
  request_group_id?: string;

  // Original items
  original_items: any[];
  
  // Exchange specific
  new_order_id?: string;
  new_items?: any[];
  
  // Return specific
  return_tracking_number?: string;
  return_payment_no?: string;
  return_validation_no?: string;
  return_logistics_id?: string;
  return_store_id?: string;
  return_store_name?: string;
  
  // Bank info
  bank_code?: string;
  bank_account?: string;

  // Logistics status
  logistics_status?: string;
  logistics_status_name?: string;
  logistics_status_updated_at?: string;

  // Exchange order fulfillment (synced from Shopify)
  exchange_fulfillment_status?: string;

  // Notes
  reason: string;
  customer_notes?: string;
  admin_notes?: string;
  
  // Order source
  order_source?: 'shopify' | 'storefront';

  // Intl (HK/MO) SF return handling — fee is charged up-front; a request whose
  // fee is unpaid is STALLED, and one whose SF waybill failed has no pickup booked.
  is_hk?: boolean;
  return_shipping_fee?: number;
  shipping_fee_paid?: boolean;
  sf_waybill_no?: string | null;
  sf_label_url?: string | null;
  sf_last_error?: string | null;
  // Exchange only: the HK→TW leg for the ORIGINAL garment. On an exchange
  // sf_waybill_no is the OUTBOUND replacement, so the customer's send-back label
  // lives here. Null on a 退貨 (there sf_waybill_no already IS the inbound leg)
  // and on a mixed group's exchange half (the return sibling carries it).
  sf_inbound_waybill_no?: string | null;
  sf_inbound_label_url?: string | null;

  // Storefront order fields (joined from orders table, storefront only)
  storefront_payment_method?: string;
  storefront_total_amount?: number | string;
  storefront_store_credit_used?: number | string | null;
  storefront_order_status?: string;
  // Sum of prior SUCCESSFUL 退刷 on this order, and what the gateway will still
  // accept. total_amount is never decremented by a partial refund, so the refund
  // default must be capped by this or a second return re-refunds the same money.
  storefront_refunded_amount?: number | string | null;
  storefront_remaining_refundable?: number | string | null;

  // Per-event refund history (credit card / LINE Pay 退刷)
  refund_history?: Array<{
    id: number;
    order_id: string;
    request_id?: string | null;
    payment_method: 'ecpg' | 'linepay' | string;
    amount: number | string;
    cumulative_refunded?: number | string | null;
    original_amount?: number | string | null;
    rtn_code?: string | null;
    rtn_message?: string | null;
    created_at: string;
    created_by?: string | null;
  }>;

  // Store-credit refund (退款轉購物金)
  customer_id?: number | null;
  refund_method?: 'original' | 'store_credit';
  credit_bonus_pct?: number | string | null;
  credit_granted_amount?: number | null;
  credit_granted_at?: string | null;
  credit_grant_tx_id?: number | null;

  // Timestamps
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

// Shopee return types
interface ShopeeReturnItem {
  model_id: number;
  name: string;
  images: string[];
  amount: number;
  item_price: number;
  item_id: number;
  item_sku: string;
  variation_sku: string;
  variant_name?: string;
  variant_parts?: { tier_name: string; option: string }[];
}

interface ShopeeReturn {
  return_sn: string;
  order_sn: string;
  status: string;
  reason: string;
  text_reason: string;
  refund_amount: number;
  currency: string;
  create_time: number;
  update_time: number;
  tracking_number: string;
  needs_logistics: boolean;
  image: string[];
  user: { username: string; email: string };
  item: ShopeeReturnItem[];
  return_solution: number;
  negotiation_status: string;
  return_refund_request_type: number;
  validation_type: string;
  reverse_logistic_status?: string;
}

interface FilterOptions {
  status: string;
  type: string;
  search: string;
  includeCompleted: boolean;
  logisticsStatus: string;
}

// 未取貨 (CVS pickup expired) order shape
interface ExpiredPickupItem {
  id: number;
  item_id: number;
  variant_id: string | null;
  title: string;
  variant_details: string | null;
  sku: string | null;
  quantity: number;
  price: number | string;
  discount_price: number | string;
  image_url: string | null;
  item_type: string;
}

interface ExpiredPickupOrder {
  order_id: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string | null;
  total_amount: number | string;
  payment_method: string;
  order_status: string;
  order_status_message: string | null;
  delivery_method: string;
  logistics_sub_type: string | null;
  store_id: string | null;
  store_name: string | null;
  store_address: string | null;
  created_at: string;
  pickup_expired_resolution: string | null;
  pickup_expired_resolved_at: string | null;
  pickup_expired_admin_notes: string | null;
  logistics_status: string;
  logistics_status_msg: string | null;
  logistics_updated_at: string | null;
  tracking_number: string | null;
  // Rescue flow fields (backend may not return these yet — all optional)
  rescue_email_sent_at?: string | null;
  rescue_response_action?: string | null;
  rescue_responded_at?: string | null;
  rescue_new_store_id?: string | null;
  rescue_new_store_name?: string | null;
  rescue_new_store_address?: string | null;
  rescue_new_logistics_sub_type?: string | null;
  rescue_new_receiver_name?: string | null;
  rescue_new_receiver_phone?: string | null;
  rescue_is_paid?: boolean | null;
  items: ExpiredPickupItem[];
}

// CVS carriers offered when rebuilding an expired pickup as a new order.
// Same list the manual-order page uses (ManualStorefrontOrderPage.tsx).
const RESCUE_LOGISTICS_OPTIONS: { value: string; label: string }[] = [
  { value: 'UNIMARTC2C', label: '7-ELEVEN 賣貨便' },
  { value: 'FAMIC2C', label: '全家 店到店' },
  { value: 'HILIFEC2C', label: '萊爾富 店到店' },
  { value: 'OKMARTC2C', label: 'OK 超商 店到店' },
];

// Unit price actually charged on an order line (discount_price wins when set).
const expiredItemUnitPrice = (it: ExpiredPickupItem): number =>
  Number(it.discount_price || it.price) || 0;

// Sum of the selected lines — the amount to collect on the rescue order.
const sumExpiredItems = (items: ExpiredPickupItem[], keys: Set<number>): number =>
  items.reduce(
    (acc, it, idx) => (keys.has(idx) ? acc + expiredItemUnitPrice(it) * (Number(it.quantity) || 1) : acc),
    0
  );

// One selected return, priced by the server. Every number here comes from
// /admin/batch-refund-preview (which calls the same computeStoreCreditRefund the
// single 購物金 grant uses) — the browser never computes a refund amount.
interface RefundPlan {
  requestId: string;
  orderId: string | null;
  customerName: string | null;
  paymentMethod: string | null;
  lane: 'card' | 'linepay' | 'store_credit' | 'manual' | null;
  cashRefund: number;
  creditGrant: number;
  creditRestore: number;
  shippingDeduction: number;
  gateway?: { remaining: number; original: number; refunded: number } | null;
  bank?: { code: string | null; account: string | null; name: string | null } | null;
  warnings: string[];
  capped?: boolean;
  eligible: boolean;
  blockedReason: string | null;
}

const GATEWAY_LANES = ['card', 'linepay', 'store_credit'] as const;

const LANE_LABEL: Record<string, string> = {
  card: '信用卡退刷',
  linepay: 'LINE Pay 退刷',
  store_credit: '核發購物金',
  manual: '匯款 (人工)',
};

const ExchangeReturnManagementPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Top-level tab: 0 = storefront (官網), 1 = Shopee
  const [mainTab, setMainTab] = useState(0);

  // State
  const [requests, setRequests] = useState<ExchangeReturnRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ExchangeReturnRequest | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  // Mixed-submission sibling highlight (合併申請): row briefly highlighted after jump
  const [highlightedRowId, setHighlightedRowId] = useState<number | null>(null);

  // Filters
  const [filters, setFilters] = useState<FilterOptions>({
    status: 'all',
    type: 'all',
    search: '',
    includeCompleted: false,
    logisticsStatus: 'all'
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Alert
  const [alert, setAlert] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
  }>({ show: false, type: 'info', message: '' });

  // Status update
  const [updateStatusDialogOpen, setUpdateStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  // Refund (credit card / LINE Pay)
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundTargetRequest, setRefundTargetRequest] = useState<ExchangeReturnRequest | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundProcessing, setRefundProcessing] = useState(false);
  // 歸還促銷購物金 — tick for seller-fault returns to re-grant the promo-funded slice (90d)
  const [refundRestorePromo, setRefundRestorePromo] = useState(false);

  // ---- Batch refund (multi-select) ---------------------------------------
  // Refunding returns one at a time was the bottleneck. Selection feeds a
  // server-priced preview; execution then loops the SAME single-request
  // endpoints, so every existing guard (gateway remaining-refundable cap,
  // idempotency, 購物金 restore, payment_refunds audit) still runs per request.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchPlans, setBatchPlans] = useState<RefundPlan[]>([]);
  const [batchTotals, setBatchTotals] = useState<any>(null);
  const [batchRestorePromo, setBatchRestorePromo] = useState(false);
  const [bankFileLoading, setBankFileLoading] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchDone, setBatchDone] = useState(false);
  const [batchCurrent, setBatchCurrent] = useState<string | null>(null);
  const [batchResults, setBatchResults] = useState<Record<string, { ok: boolean; message: string }>>({});

  // Grant store credit (退款轉購物金)
  const [grantCreditDialogOpen, setGrantCreditDialogOpen] = useState(false);
  const [grantCreditTarget, setGrantCreditTarget] = useState<ExchangeReturnRequest | null>(null);
  const [grantCreditProjection, setGrantCreditProjection] = useState<{
    totalCredit: number;
    base: number;
    bonus: number;
    effectiveBonusPct: number;
    cashRatio: number;
    // Origin split (integers) — lets the dialog recompute the total live as the
    // 歸還促銷購物金 checkbox toggles, without a round-trip.
    cashPortion: number;
    moneyCreditPortion: number;
    promoCreditPortion: number;
  } | null>(null);
  const [grantRestorePromo, setGrantRestorePromo] = useState(false);
  const [grantCreditLoading, setGrantCreditLoading] = useState(false);
  const [grantCreditProcessing, setGrantCreditProcessing] = useState(false);

  // ============ SHOPEE RETURNS STATE ============
  const [shopeeReturns, setShopeeReturns] = useState<ShopeeReturn[]>([]);
  const [shopeeLoading, setShopeeLoading] = useState(false);
  const [shopeeStatusFilter, setShopeeStatusFilter] = useState('all');
  const [shopeeSearch, setShopeeSearch] = useState('');
  const [addToOutletDialog, setAddToOutletDialog] = useState<{ open: boolean; item: ShopeeReturnItem | null; returnSn: string }>({
    open: false, item: null, returnSn: ''
  });
  const [outletDiscountPrice, setOutletDiscountPrice] = useState('');
  const [outletQuantity, setOutletQuantity] = useState(1);
  const [addingToOutlet, setAddingToOutlet] = useState(false);
  const [selectedShopeeItems, setSelectedShopeeItems] = useState<Set<string>>(new Set());
  const [batchOutletDialog, setBatchOutletDialog] = useState(false);
  const [batchDiscountPercent, setBatchDiscountPercent] = useState('40');
  const [batchAdding, setBatchAdding] = useState(false);

  // Storefront return → outlet (mirrors Shopee outlet flow but for storefront returns)
  const [sfOutletDialog, setSfOutletDialog] = useState<{
    open: boolean;
    item: any | null;
    request: ExchangeReturnRequest | null;
  }>({ open: false, item: null, request: null });
  const [sfOutletDiscountPercent, setSfOutletDiscountPercent] = useState('40');
  const [sfOutletQuantity, setSfOutletQuantity] = useState(1);
  const [addingSfOutlet, setAddingSfOutlet] = useState(false);

  // Batch add storefront return items to outlet
  const [sfBatchOutletDialog, setSfBatchOutletDialog] = useState<{
    open: boolean;
    request: ExchangeReturnRequest | null;
  }>({ open: false, request: null });
  const [sfBatchSelectedKeys, setSfBatchSelectedKeys] = useState<Set<number>>(new Set());
  const [sfBatchDiscountPercent, setSfBatchDiscountPercent] = useState('40');
  const [sfBatchAdding, setSfBatchAdding] = useState(false);

  // ============ EXPIRED PICKUP (未取貨) STATE ============
  const [expiredPickups, setExpiredPickups] = useState<ExpiredPickupOrder[]>([]);
  const [expiredStats, setExpiredStats] = useState<{ pending: number; resolved: number; total: number }>({ pending: 0, resolved: 0, total: 0 });
  const [expiredLoading, setExpiredLoading] = useState(false);
  const [expiredFilter, setExpiredFilter] = useState<'pending' | 'resolved' | 'all'>('pending');
  const [expiredSearch, setExpiredSearch] = useState('');
  const [expiredDetailOpen, setExpiredDetailOpen] = useState(false);
  const [expiredDetailOrder, setExpiredDetailOrder] = useState<ExpiredPickupOrder | null>(null);
  const [expiredResolveDialogOpen, setExpiredResolveDialogOpen] = useState(false);
  const [expiredResolveTarget, setExpiredResolveTarget] = useState<ExpiredPickupOrder | null>(null);
  const [expiredResolution, setExpiredResolution] = useState<string>('');
  const [expiredResolveNotes, setExpiredResolveNotes] = useState('');
  const [expiredResolveSaving, setExpiredResolveSaving] = useState(false);

  // ============ 挽回 → 建立新訂單 (rescue an expired pickup as a fresh order) ============
  const [rescueDialogOpen, setRescueDialogOpen] = useState(false);
  const [rescueTarget, setRescueTarget] = useState<ExpiredPickupOrder | null>(null);
  const [rescueDelivery, setRescueDelivery] = useState<'pickup' | 'shipping'>('pickup');
  const [rescueReceiverName, setRescueReceiverName] = useState('');
  const [rescueReceiverPhone, setRescueReceiverPhone] = useState('');
  const [rescueLogisticsSubType, setRescueLogisticsSubType] = useState('UNIMARTC2C');
  const [rescueStoreId, setRescueStoreId] = useState('');
  const [rescueStoreName, setRescueStoreName] = useState('');
  const [rescueStoreAddress, setRescueStoreAddress] = useState('');
  const [rescueShippingAddress, setRescueShippingAddress] = useState('');
  const [rescuePaymentMode, setRescuePaymentMode] = useState<'no_charge' | 'prepaid' | 'cod'>('cod');
  const [rescueAmount, setRescueAmount] = useState('');
  const [rescueNote, setRescueNote] = useState('');
  const [rescueItemKeys, setRescueItemKeys] = useState<Set<number>>(new Set());
  const [rescueCreating, setRescueCreating] = useState(false);
  const [rescueCreatedOrderId, setRescueCreatedOrderId] = useState<string | null>(null);

  // Fetch requests
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/exchange-return/admin/requests', {
        params: {
          status: filters.status,
          type: filters.type,
          search: filters.search,
          includeCompleted: filters.includeCompleted.toString(), 
          logisticsStatus: filters.logisticsStatus,
          limit: rowsPerPage,
          offset: page * rowsPerPage
        }
      });

      if (response.data.success) {
        setRequests(response.data.requests);
        setTotalCount(response.data.pagination.total);
      }
    } catch (error: any) {
      showAlert('error', '獲取申請列表失敗');
      console.error('Error fetching requests:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRequests();
  }, [filters, page, rowsPerPage]);

  // Utility functions
  const showAlert = (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert(prev => ({ ...prev, show: false })), 5000);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string): 'default' | 'warning' | 'info' | 'success' | 'error' => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'info';
      case 'processing': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string, requestType?: string, exchangeFulfillment?: string): string => {
    switch (status) {
      case 'pending': return '待處理';
      case 'approved': return '已批准';
      case 'processing':
        if (requestType === 'exchange' && exchangeFulfillment === 'fulfilled') return '已發貨';
        return '處理中';
      case 'completed':
        if (requestType === 'return') return '已退款';
        if (requestType === 'exchange') return '已完成';
        return '已完成';
      case 'cancelled': return '已取消';
      default: return status;
    }
  };

  const getStatusColorWithFulfillment = (status: string, requestType?: string, exchangeFulfillment?: string): 'default' | 'warning' | 'info' | 'success' | 'error' => {
    if (status === 'processing' && requestType === 'exchange' && exchangeFulfillment === 'fulfilled') return 'success';
    return getStatusColor(status);
  };

  const getTypeLabel = (type: string): string => {
    return type === 'exchange' ? '換貨' : '退貨';
  };

  const getTypeColor = (type: string): 'primary' | 'secondary' => {
    return type === 'exchange' ? 'primary' : 'secondary';
  };

  // Mixed submission (合併申請): sibling rows share request_group_id. Both rows
  // belong to the same order and normally appear in the same loaded list, so
  // matching client-side is sufficient.
  const getGroupSiblings = (request: ExchangeReturnRequest): ExchangeReturnRequest[] => {
    if (!request.request_group_id) return [];
    return requests.filter(
      r => r.request_group_id === request.request_group_id && r.id !== request.id
    );
  };

  // Jump to (expand + highlight + scroll) a sibling request in the loaded list.
  const jumpToSibling = (request: ExchangeReturnRequest) => {
    const siblings = getGroupSiblings(request);
    if (siblings.length === 0) {
      showAlert('warning', '同組的另一筆申請不在目前清單中，請調整篩選條件後再試');
      return;
    }
    const target = siblings[0];
    setExpandedRow(target.id);
    setHighlightedRowId(target.id);
    // Scroll the sibling row into view after render.
    setTimeout(() => {
      const el = document.getElementById(`err-row-${target.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    // Clear the highlight after a short window.
    setTimeout(() => setHighlightedRowId(prev => (prev === target.id ? null : prev)), 2500);
  };

  const logisticsStatusMap: Record<string, { label: string; color: 'default' | 'info' | 'warning' | 'success' | 'error' }> = {
    '300': { label: '訂單處理中', color: 'default' },
    '2030': { label: '商品已送至物流中心', color: 'info' },
    '2063': { label: '商品已送達門市', color: 'warning' },
    '2073': { label: '商品已送達門市', color: 'warning' },
    '2067': { label: '消費者成功取件', color: 'success' },
    '2068': { label: '消費者成功取件(退貨)', color: 'success' },
    '2074': { label: '消費者七天未取件', color: 'error' },
    '2098': { label: '包裹重新配達取件門市', color: 'info' },
    '2099': { label: '包裹重新配達寄件門市', color: 'info' },
    '3018': { label: '商品已送達門市', color: 'warning' },
    '3022': { label: '消費者成功取件', color: 'success' },
    '3020': { label: '消費者七天未取件', color: 'error' },
    '3024': { label: '商品已送至物流中心', color: 'info' },
    '7013': { label: '未寄件', color: 'error' },
    '1': { label: '成功', color: 'success' },
  };

  const getLogisticsStatusLabel = (status: string, fallbackName?: string): string => {
    return logisticsStatusMap[status]?.label || fallbackName || `狀態碼: ${status}`;
  };

  const getLogisticsStatusColor = (status: string): 'default' | 'info' | 'warning' | 'success' | 'error' => {
    return logisticsStatusMap[status]?.color || 'default';
  };

  // Intl (HK/MO/SG) parcels come back via SF, not 7-11, so the ECPay columns are
  // always empty for them. The customer's send-back waybill is sf_waybill_no on a
  // 退貨 row, but sf_inbound_waybill_no on an exchange (there sf_waybill_no is the
  // OUTBOUND replacement).
  const getSfSendBackWaybill = (r: ExchangeReturnRequest): string | null => {
    if (!r.is_hk) return null;
    return (r.request_type === 'return' ? r.sf_waybill_no : r.sf_inbound_waybill_no) || null;
  };
  const getSfSendBackLabel = (r: ExchangeReturnRequest): string | null => {
    if (!r.is_hk) return null;
    return (r.request_type === 'return' ? r.sf_label_url : r.sf_inbound_label_url) || null;
  };

  // Our SF credentials have no route-query permission (IUOP_QUERY_ORDER only echoes
  // the booking), so parcel movement is checked on SF's public tracking page.
  const sfTrackingUrl = (waybillNo: string): string =>
    `https://htm.sf-express.com/hk/tc/dynamic_function/waybill/waybill_query/result.html?seqNumber=${encodeURIComponent(waybillNo)}`;

  // A standalone intl 退貨 never pays the SF fee by card — it is deducted from the
  // refund — so shipping_fee_paid stays FALSE forever on it. Exchanges and mixed
  // groups pay up front.
  const isSfFeeDeductedFromRefund = (r: ExchangeReturnRequest): boolean =>
    r.request_type === 'return' && !r.request_group_id;

  const calculateRefundAmount = (items: any[]): number => {
    if (!items || items.length === 0) return 0;
    return items.reduce((total: number, item: any) => {
      // Prefer the actually-paid amount if backend provided it (storefront returns).
      const paidLine = parseFloat(item.paidLineSubtotal);
      if (Number.isFinite(paidLine) && paidLine > 0) return total + paidLine;
      const paidUnit = parseFloat(item.paidPrice);
      if (Number.isFinite(paidUnit) && paidUnit > 0) {
        return total + paidUnit * (parseInt(item.quantity) || 1);
      }
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 1;
      return total + (price * quantity);
    }, 0);
  };

  const handleQueryLogisticsStatus = async (requestId: string) => {
    try {
      const response = await api.post(`/api/exchange-return/admin/requests/${requestId}/query-logistics`);
      if (response.data.success) {
        showAlert('success', `物流狀態: ${response.data.logistics_status_name || response.data.logistics_status}`);
        fetchRequests();
      } else {
        showAlert('warning', response.data.message || '查詢物流狀態失敗');
      }
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || '查詢物流狀態失敗');
    }
  };

  // Book the missing HK→TW leg of an intl exchange (the label the customer uses to
  // send the original garment back). Idempotent server-side — an existing waybill is
  // returned rather than re-booked, so a double click can't buy a second pickup.
  const handleBookExchangeInbound = async (requestId: string) => {
    if (!window.confirm('確定要建立換貨的順豐回程單（客戶寄回原商品）？此操作會向順豐下單。')) return;
    try {
      const response = await api.post(`/api/exchange-return/admin/requests/${requestId}/hk-inbound-label`);
      if (response.data.success) {
        showAlert(
          response.data.alreadyExists ? 'info' : 'success',
          `${response.data.alreadyExists ? '回程單已存在' : '回程單已建立'}：${response.data.sfWaybillNo}`
        );
        fetchRequests();
      } else {
        showAlert('warning', response.data.error || '建立回程單失敗');
      }
    } catch (error: any) {
      showAlert('error', error.response?.data?.error || error.response?.data?.message || '建立回程單失敗');
    }
  };

  // Handle view details
  const handleViewDetails = (request: ExchangeReturnRequest) => {
    setSelectedRequest(request);
    setDetailsOpen(true);
  };

  // Handle update status
  const handleUpdateStatus = async () => {
    if (!selectedRequest) return;

    try {
      const response = await api.put(
        `/api/exchange-return/admin/requests/${selectedRequest.request_id}`,
        {
          status: newStatus,
          adminNotes: adminNotes
        }
      );

      if (response.data.success) {
        showAlert('success', '狀態更新成功');
        setUpdateStatusDialogOpen(false);
        setDetailsOpen(false);
        // Update local state instead of refetching the whole list, so the table
        // doesn't flash / scroll-reset / collapse on every status change.
        const updated = response.data.request;
        const patch = { status: newStatus, admin_notes: adminNotes };
        // A row should drop out of the current view if its new status no longer
        // matches the active filters (e.g. 已完成 with "隱藏已完成" on, or a status
        // filter that excludes the new status) — mirror the backend's list query.
        const stillVisible =
          (filters.status === 'all' || filters.status === newStatus) &&
          (filters.includeCompleted || newStatus !== 'completed');
        setRequests(prev => {
          if (!stillVisible) {
            return prev.filter(r => r.request_id !== selectedRequest.request_id);
          }
          return prev.map(r =>
            r.request_id === selectedRequest.request_id
              ? { ...r, ...(updated || {}), ...patch }
              : r
          );
        });
        if (!stillVisible) {
          setTotalCount(c => Math.max(0, c - 1));
        }
        setSelectedRequest(prev =>
          prev && prev.request_id === selectedRequest.request_id
            ? { ...prev, ...(updated || {}), ...patch }
            : prev
        );
      }
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || '更新狀態失敗');
    }
  };

  // Open refund dialog. Default to the returned-items subtotal so partial
  // returns don't accidentally refund the full order; fall back to the order
  // total only when item lines are missing.
  const openRefundDialog = (request: ExchangeReturnRequest) => {
    setRefundTargetRequest(request);
    // Prefer the actually-paid total (price × ratio) over regular-price total,
    // so refund_amount defaults to the discounted figure not the MSRP.
    const itemsTotal = calculateRefundAmount(request.original_items || []);
    const fallback = Number(request.storefront_total_amount || 0);
    let pref = itemsTotal > 0 ? itemsTotal : fallback;
    // Never default above what the gateway will still take. Prior partial refunds
    // (斷貨 lines, an earlier return) are already off the trade even though
    // total_amount still shows the original figure.
    const cap = Number(request.storefront_remaining_refundable);
    if (Number.isFinite(cap) && cap >= 0) pref = Math.min(pref, cap);
    setRefundAmount(pref > 0 ? String(Math.round(pref)) : '');
    setRefundRestorePromo(false);
    setRefundDialogOpen(true);
  };

  // Handle refund (LINE Pay / credit card)
  const handleRefund = async () => {
    if (!refundTargetRequest) return;
    const numAmount = Number(refundAmount);
    if (!numAmount || numAmount <= 0) {
      showAlert('error', '請輸入有效的退刷金額');
      return;
    }
    const cap = Number(refundTargetRequest.storefront_remaining_refundable);
    if (Number.isFinite(cap) && numAmount > cap) {
      showAlert('error', `退刷金額超過可退餘額 NT$${cap.toLocaleString()}（此訂單已退 NT$${Number(refundTargetRequest.storefront_refunded_amount || 0).toLocaleString()}）`);
      return;
    }
    const orderId = refundTargetRequest.original_order_number;
    const method = (refundTargetRequest.storefront_payment_method || '').toLowerCase();
    const refundUrl = method === 'line_pay' || method === 'linepay'
      ? `/api/linepay/refund/${orderId}`
      : `/api/ecpg/payment/refund/${orderId}`;

    setRefundProcessing(true);
    try {
      const res = await api.post(refundUrl, {
        refundAmount: numAmount,
        requestId: refundTargetRequest.request_id,
        restorePromoCredit: refundRestorePromo
      });
      if (res.data?.success) {
        showAlert('success', `退刷成功 NT$${numAmount.toLocaleString()}`);
        setRefundDialogOpen(false);
        setRefundTargetRequest(null);
        setRefundAmount('');
        fetchRequests();
      } else {
        showAlert('error', res.data?.message || '退刷失敗');
      }
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || error.message || '退刷失敗');
    }
    setRefundProcessing(false);
  };

  // Open grant-credit dialog — fetches the authoritative projection from backend
  // (same code path as the actual grant) so the displayed amount matches what
  // we'll commit. Admin confirms in the dialog before the grant fires.
  const openGrantCreditDialog = async (request: ExchangeReturnRequest) => {
    setGrantCreditTarget(request);
    setGrantCreditProjection(null);
    setGrantRestorePromo(false);
    setGrantCreditDialogOpen(true);
    setGrantCreditLoading(true);
    try {
      const res = await api.get(`/api/exchange-return/admin/requests/${request.request_id}/credit-projection`);
      if (res.data?.success && res.data.projection) {
        const p = res.data.projection;
        setGrantCreditProjection({
          totalCredit: p.totalCredit,
          base: p.breakdown?.base ?? Math.round(p.paidSubtotal || 0),
          bonus: p.breakdown?.bonus ?? 0,
          effectiveBonusPct: p.breakdown?.effectiveBonusPct ?? 0,
          cashRatio: p.breakdown?.cashRatio ?? 1,
          cashPortion: p.cashPortion ?? p.breakdown?.cashPortion ?? Math.round((p.breakdown?.base ?? 0) * (p.breakdown?.cashRatio ?? 1)),
          moneyCreditPortion: p.moneyCreditPortion ?? p.breakdown?.moneyCreditPortion ?? 0,
          promoCreditPortion: p.promoCreditPortion ?? p.breakdown?.promoCreditPortion ?? 0,
        });
      } else {
        showAlert('error', res.data?.message || '無法計算購物金金額');
        setGrantCreditDialogOpen(false);
      }
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || error.message || '計算購物金失敗');
      setGrantCreditDialogOpen(false);
    } finally {
      setGrantCreditLoading(false);
    }
  };

  const handleGrantCredit = async () => {
    if (!grantCreditTarget) return;
    setGrantCreditProcessing(true);
    try {
      const res = await api.post(
        `/api/exchange-return/admin/requests/${grantCreditTarget.request_id}/grant-credit`,
        { restorePromoCredit: grantRestorePromo }
      );
      if (res.data?.success) {
        const amt = res.data.grant?.amount || grantCreditProjection?.totalCredit || 0;
        showAlert(
          'success',
          res.data.alreadyGranted
            ? '已核發過購物金'
            : `已核發 NT$${amt.toLocaleString()} 購物金`
        );
        setGrantCreditDialogOpen(false);
        setGrantCreditTarget(null);
        setGrantCreditProjection(null);
        fetchRequests();
      } else {
        showAlert('error', res.data?.message || '核發購物金失敗');
      }
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || error.message || '核發購物金失敗');
    } finally {
      setGrantCreditProcessing(false);
    }
  };

  // ============ BATCH REFUND ============

  // Only open storefront RETURNS can be batched. 換貨 has no refund, and a
  // completed/cancelled request must not be re-paid.
  const isBatchable = (r: ExchangeReturnRequest): boolean =>
    r.request_type === 'return' &&
    r.status !== 'completed' &&
    r.status !== 'cancelled';

  const batchableOnPage = requests.filter(isBatchable);
  const allBatchableSelected =
    batchableOnPage.length > 0 &&
    batchableOnPage.every(r => selectedIds.includes(r.request_id));

  const toggleSelected = (requestId: string) => {
    setSelectedIds(prev =>
      prev.includes(requestId) ? prev.filter(id => id !== requestId) : [...prev, requestId]
    );
  };

  const toggleSelectAllOnPage = () => {
    if (allBatchableSelected) {
      const ids = new Set(batchableOnPage.map(r => r.request_id));
      setSelectedIds(prev => prev.filter(id => !ids.has(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...batchableOnPage.map(r => r.request_id)])));
    }
  };

  // Price the selection server-side. Read-only — nothing moves until Magnus
  // presses a button inside the dialog.
  const loadBatchPreview = async (restorePromo: boolean) => {
    setBatchLoading(true);
    try {
      const res = await api.post('/api/exchange-return/admin/batch-refund-preview', {
        requestIds: selectedIds,
        restorePromoCredit: restorePromo,
      });
      if (res.data?.success) {
        setBatchPlans(res.data.plans || []);
        setBatchTotals(res.data.totals || null);
      } else {
        showAlert('error', res.data?.message || '計算批次退款失敗');
        setBatchOpen(false);
      }
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || error.message || '計算批次退款失敗');
      setBatchOpen(false);
    } finally {
      setBatchLoading(false);
    }
  };

  const openBatchDialog = async () => {
    if (selectedIds.length === 0) return;
    setBatchPlans([]);
    setBatchTotals(null);
    setBatchResults({});
    setBatchDone(false);
    setBatchRestorePromo(false);
    setBatchCurrent(null);
    setBatchOpen(true);
    await loadBatchPreview(false);
  };

  const gatewayPlans = batchPlans.filter(
    p => p.eligible && p.lane && (GATEWAY_LANES as readonly string[]).includes(p.lane)
  );
  const manualPlans = batchPlans.filter(p => p.eligible && p.lane === 'manual');
  const blockedPlans = batchPlans.filter(p => !p.eligible);

  // Execute one lane per request, sequentially. Sequential (not Promise.all) on
  // purpose: a gateway that rate-limits or half-fails must not leave a dozen
  // in-flight refunds whose outcome nobody can read.
  const runBatchRefund = async () => {
    if (gatewayPlans.length === 0) return;
    setBatchRunning(true);
    const results: Record<string, { ok: boolean; message: string }> = { ...batchResults };
    const succeededCash: string[] = [];

    for (const plan of gatewayPlans) {
      if (results[plan.requestId]?.ok) continue; // never re-pay on a re-run
      setBatchCurrent(plan.requestId);
      try {
        if (plan.lane === 'store_credit') {
          const res = await api.post(
            `/api/exchange-return/admin/requests/${plan.requestId}/grant-credit`,
            { restorePromoCredit: batchRestorePromo }
          );
          results[plan.requestId] = res.data?.success
            ? { ok: true, message: res.data.alreadyGranted ? '已核發過' : `已核發 NT$${(res.data.grant?.amount ?? plan.creditGrant).toLocaleString()}` }
            : { ok: false, message: res.data?.message || '核發失敗' };
        } else {
          const url = plan.lane === 'linepay'
            ? `/api/linepay/refund/${plan.orderId}`
            : `/api/ecpg/payment/refund/${plan.orderId}`;
          const res = await api.post(url, {
            refundAmount: plan.cashRefund,
            requestId: plan.requestId,
            restorePromoCredit: batchRestorePromo,
          });
          if (res.data?.success) {
            results[plan.requestId] = { ok: true, message: `已退刷 NT$${plan.cashRefund.toLocaleString()}` };
            // A short-paid (gateway-capped) refund is NOT finished — leave its
            // status open so it stays visible instead of silently closing.
            if (!plan.capped) succeededCash.push(plan.requestId);
          } else {
            results[plan.requestId] = { ok: false, message: res.data?.message || '退刷失敗' };
          }
        }
      } catch (error: any) {
        results[plan.requestId] = {
          ok: false,
          message: error.response?.data?.message || error.message || '失敗',
        };
      }
      setBatchResults({ ...results });
    }

    // Fully-refunded cash lanes → 退款完成. (grant-credit already completes its own.)
    if (succeededCash.length > 0) {
      try {
        await api.post('/api/exchange-return/admin/batch-mark-refunded', {
          requestIds: succeededCash,
          note: '批次退刷成功後自動標記',
        });
      } catch (error: any) {
        showAlert('warning', `退刷已完成，但標記退款完成失敗：${error.response?.data?.message || error.message}`);
      }
    }

    setBatchCurrent(null);
    setBatchRunning(false);
    setBatchDone(true);
    const okCount = gatewayPlans.filter(p => results[p.requestId]?.ok).length;
    showAlert(
      okCount === gatewayPlans.length ? 'success' : 'warning',
      `批次完成：成功 ${okCount} / ${gatewayPlans.length} 筆`
    );
    fetchRequests();
  };

  // Money-free: flips the selected manual-lane rows to 退款完成 after Magnus has
  // transferred from the 匯款清單.
  const runBatchMarkRefunded = async () => {
    if (manualPlans.length === 0) return;
    setBatchRunning(true);
    try {
      const res = await api.post('/api/exchange-return/admin/batch-mark-refunded', {
        requestIds: manualPlans.map(p => p.requestId),
        note: '人工匯款',
      });
      const results: Record<string, { ok: boolean; message: string }> = { ...batchResults };
      (res.data?.results || []).forEach((r: any) => {
        results[r.requestId] = { ok: !!r.ok, message: r.message };
      });
      setBatchResults(results);
      setBatchDone(true);
      showAlert('success', `已標記 ${res.data?.marked ?? 0} 筆退款完成`);
      fetchRequests();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || error.message || '標記失敗');
    } finally {
      setBatchRunning(false);
    }
  };

  // Tab-separated so it pastes straight into the bank's bulk-transfer sheet.
  const copyRemittanceList = async () => {
    if (manualPlans.length === 0) return;
    const header = ['申請編號', '訂單編號', '戶名', '銀行代碼', '帳號', '金額'].join('\t');
    const lines = manualPlans.map(p => [
      p.requestId,
      p.orderId || '',
      p.bank?.name || p.customerName || '',
      p.bank?.code || '',
      p.bank?.account || '',
      String(p.cashRefund),
    ].join('\t'));
    const total = manualPlans.reduce((t, p) => t + p.cashRefund, 0);
    const text = [header, ...lines, '', `合計\t\t\t\t\t${total}`].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      showAlert('success', `已複製 ${manualPlans.length} 筆匯款資料 (合計 NT$${total.toLocaleString()})`);
    } catch {
      showAlert('error', '複製失敗，請手動選取表格內容');
    }
  };

  // 上海銀行 全球一路通 台幣整批交易 file (一扣多入, Big5). The backend re-prices
  // and encodes it; upload at 收付款服務 > 轉帳匯款 > 台幣整批交易 > 檔案匯入.
  const downloadBankFile = async () => {
    if (manualPlans.length === 0) return;
    setBankFileLoading(true);
    try {
      const res = await api.post(
        '/api/exchange-return/admin/batch-refund-bank-file',
        { requestIds: manualPlans.map(p => p.requestId), restorePromoCredit: batchRestorePromo },
        { responseType: 'blob' }
      );
      const disposition = String(res.headers?.['content-disposition'] || '');
      const filename = disposition.match(/filename="([^"]+)"/)?.[1] || 'scsb_refund.txt';
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      const total = manualPlans.reduce((t, p) => t + p.cashRefund, 0);
      const summary = `已下載銀行檔：一扣多入 · 總筆數 ${manualPlans.length} · 總金額 ${total}`;
      // Rows whose branch could not be read off the account number went to the
      // bank's 營業部 — they import fine but may come back 退匯.
      const guessed = String(res.headers?.['x-guessed-branch'] || '').split(',').filter(Boolean);
      if (guessed.length > 0) {
        showAlert('warning', `${summary}。${guessed.join('、')} 的分行是推測的（用營業部），若被退匯請改用單筆轉帳`);
      } else {
        showAlert('success', summary);
      }
    } catch (error: any) {
      // A blob response carries the JSON error as a Blob too.
      let message = error.message || '產生銀行檔失敗';
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          const body = JSON.parse(await data.text());
          message = body?.error?.message || body?.message || message;
        } catch { /* keep the transport message */ }
      }
      showAlert('error', message);
    } finally {
      setBankFileLoading(false);
    }
  };

  // ============ SHOPEE RETURNS LOGIC ============
  const fetchShopeeReturns = async () => {
    setShopeeLoading(true);
    try {
      const params: any = { all: 'true', days: 30 };
      if (shopeeStatusFilter !== 'all') params.status = shopeeStatusFilter;
      const response = await api.get('/api/shopee/returns', { params });
      if (response.data.success) {
        setShopeeReturns(response.data.returns || []);
      }
    } catch (error: any) {
      showAlert('error', '獲取蝦皮退貨列表失敗');
      console.error('Error fetching Shopee returns:', error);
    }
    setShopeeLoading(false);
  };

  useEffect(() => {
    if (mainTab === 1) {
      fetchShopeeReturns();
    } else if (mainTab === 2) {
      fetchExpiredPickups();
    }
  }, [mainTab, shopeeStatusFilter, expiredFilter]);

  // Always load expired-pickup stats on mount so the badge shows on all tabs
  useEffect(() => {
    if (mainTab !== 2) {
      api.get('/api/exchange-return/admin/expired-pickups', { params: { resolution: 'pending', limit: 1 } })
        .then(r => { if (r.data?.success && r.data.stats) setExpiredStats(r.data.stats); })
        .catch(() => { /* badge is best-effort */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============ EXPIRED PICKUP (未取貨) HELPERS ============
  const fetchExpiredPickups = async () => {
    setExpiredLoading(true);
    try {
      const response = await api.get('/api/exchange-return/admin/expired-pickups', {
        params: {
          resolution: expiredFilter,
          search: expiredSearch,
          limit: 100,
          offset: 0,
        },
      });
      if (response.data.success) {
        setExpiredPickups(response.data.orders || []);
        setExpiredStats(response.data.stats || { pending: 0, resolved: 0, total: 0 });
      }
    } catch (error: any) {
      showAlert('error', '獲取未取貨訂單失敗');
      console.error('Error fetching expired pickups:', error);
    }
    setExpiredLoading(false);
  };

  const isCodOrder = (paymentMethod: string): boolean => {
    // 取貨付款 (cash on delivery) — order is auto-cancelled, no refund needed
    // DB stores 'store_cod' (see backend/src/routes/ecpay.routes.js:2510); also handle legacy variants
    const m = (paymentMethod || '').toLowerCase();
    return m.includes('cod') || m === 'cash_on_delivery';
  };

  const formatPaymentMethod = (paymentMethod: string): string => {
    const m = (paymentMethod || '').toLowerCase();
    if (m.includes('cod') || m === 'cash_on_delivery') return '取貨付款';
    if (m.includes('credit') || m.includes('card')) return '信用卡';
    if (m.includes('linepay') || m.includes('line_pay')) return 'LINE Pay';
    if (m.includes('atm')) return 'ATM';
    return paymentMethod || '-';
  };

  const formatLogisticsSubType = (subType: string | null): string => {
    if (!subType) return '超商';
    const s = subType.toUpperCase();
    if (s.includes('UNIMART')) return '7-ELEVEN';
    if (s.includes('FAMI')) return '全家';
    if (s.includes('HILIFE')) return '萊爾富';
    if (s.includes('OKMART')) return 'OK';
    return subType;
  };

  const getResolutionLabel = (resolution: string | null): string => {
    switch (resolution) {
      case 'refunded':  return '已退款';
      case 'reshipped': return '已重寄';
      case 'outlet':    return '已加入Outlet';
      case 'closed':    return '結案';
      default:          return '待處理';
    }
  };

  const getResolutionColor = (resolution: string | null): 'default' | 'warning' | 'success' | 'info' | 'primary' => {
    switch (resolution) {
      case 'refunded':  return 'success';
      case 'reshipped': return 'info';
      case 'outlet':    return 'primary';
      case 'closed':    return 'default';
      default:          return 'warning';
    }
  };

  // Customer self-service rescue status (reship / cancel via emailed link).
  // Renders '—' when no rescue email has gone out (older orders / backend not deployed).
  const getRescueLabel = (order: ExpiredPickupOrder): string => {
    if (!order.rescue_email_sent_at) return '—';
    switch (order.rescue_response_action) {
      case 'reship': return order.rescue_is_paid ? '重寄·已付款' : '重寄·待付款';
      case 'cancel': return '已取消';
      default:       return '未回覆';
    }
  };

  const getRescueColor = (order: ExpiredPickupOrder): 'default' | 'warning' | 'success' | 'info' => {
    if (!order.rescue_email_sent_at) return 'default';
    switch (order.rescue_response_action) {
      case 'reship': return order.rescue_is_paid ? 'success' : 'info';
      case 'cancel': return 'default';
      default:       return 'warning';
    }
  };

  const openResolveDialog = (order: ExpiredPickupOrder) => {
    setExpiredResolveTarget(order);
    setExpiredResolution(order.pickup_expired_resolution || '');
    setExpiredResolveNotes(order.pickup_expired_admin_notes || '');
    setExpiredResolveDialogOpen(true);
  };

  const handleResolveExpired = async () => {
    if (!expiredResolveTarget) return;
    setExpiredResolveSaving(true);
    try {
      const response = await api.patch(
        `/api/exchange-return/admin/expired-pickups/${encodeURIComponent(expiredResolveTarget.order_id)}/resolve`,
        { resolution: expiredResolution, notes: expiredResolveNotes }
      );
      if (response.data.success) {
        showAlert('success', '已更新處理狀態');
        setExpiredResolveDialogOpen(false);
        setExpiredResolveTarget(null);
        await fetchExpiredPickups();
      } else {
        showAlert('error', '更新失敗');
      }
    } catch (error: any) {
      showAlert('error', error?.response?.data?.message || '更新失敗');
      console.error('Resolve expired pickup error:', error);
    }
    setExpiredResolveSaving(false);
  };

  // 挽回：客戶說還是要這件 → 用原訂單的商品開一張新訂單（原包裹退回後進庫存/Outlet）。
  // Prefills from the customer's own rescue reply (new store / receiver) when they
  // used the emailed link; otherwise falls back to the original order's store.
  const openRescueDialog = (order: ExpiredPickupOrder) => {
    const wantsReship = order.rescue_response_action === 'reship';
    const paid = !isCodOrder(order.payment_method);
    const allKeys = new Set((order.items || []).map((_, idx) => idx));

    setRescueTarget(order);
    setRescueCreatedOrderId(null);
    setRescueDelivery('pickup');
    setRescueReceiverName((wantsReship && order.rescue_new_receiver_name) || order.customer_name || '');
    setRescueReceiverPhone((wantsReship && order.rescue_new_receiver_phone) || order.customer_phone || '');
    setRescueLogisticsSubType(
      (wantsReship && order.rescue_new_logistics_sub_type) || order.logistics_sub_type || 'UNIMARTC2C'
    );
    setRescueStoreId((wantsReship && order.rescue_new_store_id) || order.store_id || '');
    setRescueStoreName((wantsReship && order.rescue_new_store_name) || order.store_name || '');
    setRescueStoreAddress((wantsReship && order.rescue_new_store_address) || order.store_address || '');
    setRescueShippingAddress('');
    setRescueItemKeys(allKeys);
    // Already-paid original → don't charge again on the new order (the money sits on
    // the original order; charging again would double-count revenue). COD original →
    // nothing was collected, so the new order carries the amount.
    setRescuePaymentMode(paid ? 'no_charge' : 'cod');
    setRescueAmount(String(sumExpiredItems(order.items || [], allKeys) || Math.round(Number(order.total_amount) || 0)));
    setRescueNote(`未取貨挽回 — 原訂單 ${order.order_id}`);
    setRescueDialogOpen(true);
  };

  const toggleRescueItem = (idx: number) => {
    const next = new Set(rescueItemKeys);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    setRescueItemKeys(next);
    // Keep the charge in sync with the selection unless we're not charging at all.
    if (rescueTarget && rescuePaymentMode !== 'no_charge') {
      setRescueAmount(String(sumExpiredItems(rescueTarget.items || [], next)));
    }
  };

  const handleCreateRescueOrder = async () => {
    const order = rescueTarget;
    if (!order) return;

    const items = (order.items || []).filter((_, idx) => rescueItemKeys.has(idx));
    if (items.length === 0) { showAlert('warning', '請至少選擇一件商品'); return; }
    if (!rescueReceiverName.trim()) { showAlert('warning', '請填寫收件人姓名'); return; }
    if (!rescueReceiverPhone.trim()) { showAlert('warning', '請填寫收件人電話'); return; }
    if (rescueDelivery === 'pickup' && !rescueStoreId.trim()) { showAlert('warning', '超商取貨需要門市店號'); return; }
    if (rescueDelivery === 'shipping' && !rescueShippingAddress.trim()) { showAlert('warning', '宅配需要收件地址'); return; }
    if (rescuePaymentMode === 'cod' && rescueDelivery !== 'pickup') { showAlert('warning', '貨到付款只能搭配超商取貨'); return; }

    const amount = rescuePaymentMode === 'no_charge' ? 0 : Number(rescueAmount) || 0;
    if (rescuePaymentMode !== 'no_charge' && amount <= 0) { showAlert('warning', '請輸入收款金額'); return; }

    setRescueCreating(true);
    try {
      const { data } = await api.post('/api/storefront/orders/manual', {
        customerName: rescueReceiverName.trim(),
        customerPhone: rescueReceiverPhone.trim(),
        customerEmail: order.customer_email || null,
        deliveryMethod: rescueDelivery,
        logisticsSubType: rescueDelivery === 'pickup' ? rescueLogisticsSubType : null,
        storeId: rescueDelivery === 'pickup' ? rescueStoreId.trim() : null,
        storeName: rescueDelivery === 'pickup' ? rescueStoreName.trim() : null,
        storeAddress: rescueDelivery === 'pickup' ? rescueStoreAddress.trim() : null,
        shippingAddress: rescueDelivery === 'pickup' ? null : rescueShippingAddress.trim(),
        shippingCountry: 'TW',
        items: items.map(it => ({
          itemId: it.item_id,
          variantId: it.variant_id,
          title: it.title,
          price: expiredItemUnitPrice(it),
          quantity: it.quantity,
          sku: it.sku || '',
          imageUrl: it.image_url || '',
          variantDetails: it.variant_details || '',
        })),
        paymentMode: rescuePaymentMode,
        totalAmount: amount,
        note: rescueNote.trim() || `未取貨挽回 — 原訂單 ${order.order_id}`,
      });

      const newOrderId = data?.orderId || '';

      // Close the loop on the expired order: mark 已重寄 and record the new order id
      // in the notes (appended, so an existing note isn't lost). Non-fatal.
      try {
        const prevNotes = (order.pickup_expired_admin_notes || '').trim();
        const linkNote = `挽回新訂單 ${newOrderId}`;
        await api.patch(
          `/api/exchange-return/admin/expired-pickups/${encodeURIComponent(order.order_id)}/resolve`,
          { resolution: 'reshipped', notes: prevNotes ? `${prevNotes}\n${linkNote}` : linkNote }
        );
      } catch (linkErr: any) {
        console.error('Rescue order created but resolve-link failed:', linkErr);
        showAlert('warning', `新訂單 ${newOrderId} 已建立，但未能自動標記原訂單，請手動標記處理`);
      }

      setRescueCreatedOrderId(newOrderId);
      await fetchExpiredPickups();
    } catch (error: any) {
      showAlert('error', `建立新訂單失敗: ${error?.response?.data?.error || error?.message || '未知錯誤'}`);
      console.error('Create rescue order error:', error);
    }
    setRescueCreating(false);
  };

  const filteredShopeeReturns = shopeeReturns.filter(r => {
    if (!shopeeSearch) return true;
    const q = shopeeSearch.toLowerCase();
    return (
      r.return_sn.toLowerCase().includes(q) ||
      r.order_sn.toLowerCase().includes(q) ||
      r.user?.username?.toLowerCase().includes(q) ||
      r.item?.some(i => i.name.toLowerCase().includes(q) || i.item_sku?.toLowerCase().includes(q))
    );
  });

  const getShopeeReturnStatusColor = (status: string): 'default' | 'warning' | 'info' | 'success' | 'error' => {
    switch (status) {
      case 'REQUESTED': return 'warning';
      case 'ACCEPTED': return 'success';
      case 'CANCELLED': return 'error';
      case 'JUDGING': return 'info';
      case 'PROCESSING': return 'info';
      case 'SELLER_DISPUTE': return 'error';
      case 'REFUND_PAID': return 'success';
      case 'CLOSED': return 'default';
      default: return 'default';
    }
  };

  const getShopeeReturnStatusLabel = (status: string): string => {
    switch (status) {
      case 'REQUESTED': return '已申請';
      case 'ACCEPTED': return '已接受';
      case 'CANCELLED': return '已取消';
      case 'JUDGING': return '審核中';
      case 'PROCESSING': return '處理中';
      case 'SELLER_DISPUTE': return '賣家申訴';
      case 'REFUND_PAID': return '已退款';
      case 'CLOSED': return '已關閉';
      default: return status;
    }
  };

  const getReturnSolutionLabel = (solution: number): string => {
    return solution === 0 ? '退貨退款' : '僅退款';
  };

  const isReturnReadyForOutlet = (r: ShopeeReturn): boolean => {
    // Return is accepted/refund_paid AND solution is "return and refund" (item comes back)
    return (
      (r.status === 'ACCEPTED' || r.status === 'REFUND_PAID') &&
      r.return_solution === 0
    );
  };

  // Flatten returns into item-level rows (only outlet-ready items)
  interface FlatShopeeItem {
    key: string; // unique key for selection
    item: ShopeeReturnItem;
    returnSn: string;
    orderSn: string;
    status: string;
    reason: string;
    textReason: string;
    refundAmount: number;
    currency: string;
    createTime: number;
    username: string;
    returnImages: string[];
  }

  const outletReadyItems: FlatShopeeItem[] = filteredShopeeReturns
    .filter(r => isReturnReadyForOutlet(r))
    .flatMap(r =>
      (r.item || []).map((item, idx) => ({
        key: `${r.return_sn}-${idx}`,
        item,
        returnSn: r.return_sn,
        orderSn: r.order_sn,
        status: r.status,
        reason: r.reason,
        textReason: r.text_reason,
        refundAmount: r.refund_amount,
        currency: r.currency,
        createTime: r.create_time,
        username: r.user?.username || '-',
        returnImages: r.image || [],
      }))
    );

  const getVariantInfo = (item: ShopeeReturnItem): { color: string; size: string } => {
    // Use enriched variant_parts from backend (from get_model_list)
    if (item.variant_parts && item.variant_parts.length > 0) {
      let color = '-';
      let size = '-';
      for (const part of item.variant_parts) {
        const tierLower = part.tier_name.toLowerCase();
        if (tierLower.includes('顏色') || tierLower.includes('color') || tierLower.includes('colour')) {
          color = part.option;
        } else if (tierLower.includes('尺寸') || tierLower.includes('size') || tierLower.includes('碼')) {
          size = part.option;
        } else if (color === '-') {
          // First unknown tier = assume color
          color = part.option;
        } else {
          size = part.option;
        }
      }
      return { color, size };
    }
    // Fallback: parse from variation_sku
    if (!item.variation_sku) return { color: '-', size: '-' };
    const parts = item.variation_sku.split(/[,\-_]/);
    if (parts.length >= 2) return { color: parts[0], size: parts.slice(1).join('/') };
    return { color: item.variation_sku, size: '-' };
  };

  const toggleSelectItem = (key: string) => {
    setSelectedShopeeItems(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedShopeeItems.size === outletReadyItems.length) {
      setSelectedShopeeItems(new Set());
    } else {
      setSelectedShopeeItems(new Set(outletReadyItems.map(i => i.key)));
    }
  };

  const handleBatchAddToOutlet = async () => {
    const pct = parseFloat(batchDiscountPercent);
    if (isNaN(pct) || pct <= 0 || pct >= 100) {
      enqueueSnackbar('請輸入有效的折扣百分比 (1-99)', { variant: 'warning' });
      return;
    }

    const itemsToAdd = outletReadyItems.filter(i => selectedShopeeItems.has(i.key));
    if (itemsToAdd.length === 0) return;

    setBatchAdding(true);
    let successCount = 0;
    let failCount = 0;

    for (const flatItem of itemsToAdd) {
      try {
        const item = flatItem.item;
        let description = '';
        let imageUrl = item.images?.[0] || '';
        let allImages: string | null = null;
        let title = item.name;

        try {
          const detailRes = await api.get(`/api/shopee/products/${item.item_id}/details-for-import`);
          if (detailRes.data.success) {
            const product = detailRes.data.enhanced_data || detailRes.data.product;
            description = product.description || '';
            if (product.images && product.images.length > 0) {
              allImages = JSON.stringify(product.images.map((url: string) => ({ src: url })));
            }
            if (product.image?.image_url_list?.length > 0) {
              imageUrl = product.image.image_url_list[0];
              allImages = JSON.stringify(product.image.image_url_list.map((url: string) => ({ src: url })));
            }
            title = product.item_name || product.name || item.name;
          }
        } catch (e) {
          console.warn('Could not fetch product details for outlet:', e);
        }

        const discountPrice = Math.round(item.item_price * (1 - pct / 100));
          const variantLabel = item.variant_name || item.variation_sku || 'Default';
        const payload = {
          variant_id: item.item_id,
          sku: item.item_sku || item.variation_sku || `SHOPEE-${item.item_id}`,
          variant_details: variantLabel,
          display_sku: variantLabel !== 'Default'
            ? `${item.item_sku || ''} (${variantLabel})`.trim()
            : item.item_sku || `SHOPEE-${item.item_id}`,
          title,
          variant_title: variantLabel,
          description,
          image_url: imageUrl,
          all_images: allImages,
          price: item.item_price,
          discount_price: discountPrice,
          available_quantity: item.amount || 1,
          source: 'shopee',
        };

        await api.post('/api/outlet/add', payload);
        successCount++;
      } catch (error: any) {
        failCount++;
        console.error(`Failed to add item to outlet:`, error);
      }
    }

    if (successCount > 0) enqueueSnackbar(`已成功加入 ${successCount} 件商品到 Outlet`, { variant: 'success' });
    if (failCount > 0) enqueueSnackbar(`${failCount} 件商品加入失敗`, { variant: 'error' });
    setSelectedShopeeItems(new Set());
    setBatchOutletDialog(false);
    setBatchAdding(false);
  };

  const handleOpenAddToOutlet = (item: ShopeeReturnItem, returnSn: string) => {
    setAddToOutletDialog({ open: true, item, returnSn });
    // Prefill at 40% off (60% of original price)
    const prefillPrice = item.item_price > 0
      ? String(Math.round(item.item_price * 0.6))
      : '';
    setOutletDiscountPrice(prefillPrice);
    setOutletQuantity(item.amount || 1);
  };

  const handleAddToOutlet = async () => {
    const item = addToOutletDialog.item;
    if (!item || !outletDiscountPrice) return;

    const discountPrice = parseFloat(outletDiscountPrice);
    if (isNaN(discountPrice) || discountPrice <= 0) {
      enqueueSnackbar('請輸入有效的特價', { variant: 'warning' });
      return;
    }
    if (discountPrice >= item.item_price) {
      enqueueSnackbar('特價必須低於原價', { variant: 'warning' });
      return;
    }

    setAddingToOutlet(true);
    try {
      // Get product details for images/description
      let description = '';
      let imageUrl = item.images?.[0] || '';
      let allImages: string | null = null;
      let title = item.name;

      try {
        const detailRes = await api.get(`/api/shopee/products/${item.item_id}/details-for-import`);
        if (detailRes.data.success) {
          const product = detailRes.data.enhanced_data || detailRes.data.product;
          description = product.description || '';
          if (product.images && product.images.length > 0) {
            allImages = JSON.stringify(product.images.map((url: string) => ({ src: url })));
          }
          if (product.image?.image_url_list?.length > 0) {
            imageUrl = product.image.image_url_list[0];
            allImages = JSON.stringify(product.image.image_url_list.map((url: string) => ({ src: url })));
          }
          title = product.item_name || product.name || item.name;
        }
      } catch (e) {
        console.warn('Could not fetch product details for outlet:', e);
      }

      const variantLabel = item.variant_name || item.variation_sku || 'Default';
      const payload = {
        variant_id: item.item_id,
        sku: item.item_sku || item.variation_sku || `SHOPEE-${item.item_id}`,
        variant_details: variantLabel,
        display_sku: variantLabel !== 'Default'
          ? `${item.item_sku || ''} (${variantLabel})`.trim()
          : item.item_sku || `SHOPEE-${item.item_id}`,
        title,
        variant_title: variantLabel,
        description,
        image_url: imageUrl,
        all_images: allImages,
        price: item.item_price,
        discount_price: discountPrice,
        available_quantity: outletQuantity,
        source: 'shopee',
      };

      const response = await api.post('/api/outlet/add', payload);
      enqueueSnackbar(response.data.message || '已成功加入 Outlet!', { variant: 'success' });
      setAddToOutletDialog({ open: false, item: null, returnSn: '' });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || '加入 Outlet 失敗', { variant: 'error' });
    }
    setAddingToOutlet(false);
  };

  const handleOpenSfOutlet = (item: any, request: ExchangeReturnRequest) => {
    setSfOutletDialog({ open: true, item, request });
    setSfOutletDiscountPercent('40');
    setSfOutletQuantity(parseInt(item.quantity) || 1);
  };

  const handleOpenSfBatchOutlet = (request: ExchangeReturnRequest) => {
    const items = Array.isArray(request.original_items) ? request.original_items : [];
    setSfBatchOutletDialog({ open: true, request });
    // Pre-select every item that has a usable price
    setSfBatchSelectedKeys(new Set(
      items.map((item: any, idx: number) => (parseFloat(item.price) > 0 ? idx : -1))
        .filter((idx: number) => idx >= 0)
    ));
    setSfBatchDiscountPercent('40');
  };

  const toggleSfBatchKey = (idx: number) => {
    setSfBatchSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleAddSfBatchToOutlet = async () => {
    const request = sfBatchOutletDialog.request;
    if (!request) return;
    const pct = parseFloat(sfBatchDiscountPercent);
    if (!Number.isFinite(pct) || pct <= 0 || pct >= 100) {
      enqueueSnackbar('請輸入有效的折扣百分比 (1-99)', { variant: 'warning' });
      return;
    }
    const items = Array.isArray(request.original_items) ? request.original_items : [];
    const selected = items
      .map((item: any, idx: number) => ({ item, idx }))
      .filter(({ idx }) => sfBatchSelectedKeys.has(idx));
    if (selected.length === 0) {
      enqueueSnackbar('請至少選擇一件商品', { variant: 'warning' });
      return;
    }

    setSfBatchAdding(true);
    let successCount = 0;
    let failCount = 0;

    for (const { item } of selected) {
      const originalPrice = parseFloat(item.price) || 0;
      if (originalPrice <= 0) { failCount++; continue; }
      const discountPrice = Math.round(originalPrice * (1 - pct / 100));
      if (discountPrice <= 0) { failCount++; continue; }
      try {
        await api.post('/api/exchange-return/admin/return-add-to-outlet', {
          requestId: request.request_id,
          lineItemId: item.lineItemId,
          productName: item.productName,
          variantTitle: item.variantTitle || null,
          discountPrice,
          quantity: parseInt(item.quantity) || 1
        });
        successCount++;
      } catch (error) {
        console.error('Failed to add storefront return item to outlet:', error);
        failCount++;
      }
    }

    if (successCount > 0) enqueueSnackbar(`已成功加入 ${successCount} 件商品到 Outlet`, { variant: 'success' });
    if (failCount > 0) enqueueSnackbar(`${failCount} 件商品加入失敗`, { variant: 'error' });
    setSfBatchAdding(false);
    setSfBatchOutletDialog({ open: false, request: null });
    setSfBatchSelectedKeys(new Set());
  };

  const handleAddSfReturnToOutlet = async () => {
    const item = sfOutletDialog.item;
    const request = sfOutletDialog.request;
    if (!item || !request) return;

    const pct = parseFloat(sfOutletDiscountPercent);
    if (!Number.isFinite(pct) || pct <= 0 || pct >= 100) {
      enqueueSnackbar('請輸入有效的折扣百分比 (1-99)', { variant: 'warning' });
      return;
    }
    const originalPrice = parseFloat(item.price) || 0;
    if (originalPrice <= 0) {
      enqueueSnackbar('找不到原價資訊，無法計算特價', { variant: 'warning' });
      return;
    }
    const discountPrice = Math.round(originalPrice * (1 - pct / 100));
    if (discountPrice <= 0) {
      enqueueSnackbar('特價必須大於 0', { variant: 'warning' });
      return;
    }

    setAddingSfOutlet(true);
    try {
      const response = await api.post('/api/exchange-return/admin/return-add-to-outlet', {
        requestId: request.request_id,
        lineItemId: item.lineItemId,
        productName: item.productName,
        variantTitle: item.variantTitle || null,
        discountPrice,
        quantity: sfOutletQuantity
      });
      enqueueSnackbar(response.data.message || '已成功加入 Outlet!', { variant: 'success' });
      setSfOutletDialog({ open: false, item: null, request: null });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || '加入 Outlet 失敗', { variant: 'error' });
    }
    setAddingSfOutlet(false);
  };

  // Open update status dialog
  const openUpdateStatusDialog = (request: ExchangeReturnRequest) => {
    setSelectedRequest(request);
    setNewStatus(request.status);
    setAdminNotes(request.admin_notes || '');
    setUpdateStatusDialogOpen(true);
  };

  // Statistics
  const getStatistics = () => {
    const stats = {
      total: totalCount,
      pending: requests.filter(r => r.status === 'pending').length,
      processing: requests.filter(r => r.status === 'processing').length,
      completed: requests.filter(r => r.status === 'completed').length,
      exchange: requests.filter(r => r.request_type === 'exchange').length,
      return: requests.filter(r => r.request_type === 'return').length
    };
    return stats;
  };

  const stats = getStatistics();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          退換貨管理
        </Typography>
        <Typography variant="body2" color="text.secondary">
          管理和追蹤所有退換貨申請
        </Typography>
      </Box>

      {/* Alert */}
      {alert.show && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(prev => ({ ...prev, show: false }))}>
          {alert.message}
        </Alert>
      )}

      {/* Main Platform Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          sx={{
            '& .MuiTab-root': { fontWeight: 'bold', fontSize: '0.95rem' },
          }}
        >
          <Tab label="官網退換貨" icon={<Storefront />} iconPosition="start" />
          <Tab label="蝦皮退貨" icon={<ShoppingCart />} iconPosition="start" />
          <Tab
            label={
              <Badge
                color="error"
                badgeContent={expiredStats.pending}
                max={99}
                sx={{ '& .MuiBadge-badge': { right: -14, top: 4 } }}
              >
                未取貨
              </Badge>
            }
            icon={<EventBusy />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* ============ TAB 0: SHOPIFY (existing content) ============ */}
      {mainTab === 0 && (<>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                總申請數
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                待處理
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                處理中
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {stats.processing}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                已完成
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {stats.completed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                換貨
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                {stats.exchange}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                退貨
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="secondary.main">
                {stats.return}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="搜尋訂單編號、申請編號、客戶..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>狀態</InputLabel>
              <Select
                value={filters.status}
                label="狀態"
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="all">全部</MenuItem>
                <MenuItem value="pending">待處理</MenuItem>
                <MenuItem value="approved">已批准</MenuItem>
                <MenuItem value="processing">處理中</MenuItem>
                <MenuItem value="completed">已完成</MenuItem>
                <MenuItem value="cancelled">已取消</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>類型</InputLabel>
              <Select
                value={filters.type}
                label="類型"
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              >
                <MenuItem value="all">全部</MenuItem>
                <MenuItem value="exchange">換貨</MenuItem>
                <MenuItem value="return">退貨</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {/* 物流狀態 — filters on the raw ECPay code behind the table's 物流狀態 chip */}
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>物流狀態</InputLabel>
              <Select
                value={filters.logisticsStatus}
                label="物流狀態"
                onChange={(e) => { setPage(0); setFilters(prev => ({ ...prev, logisticsStatus: e.target.value })); }}
              >
                <MenuItem value="all">全部</MenuItem>
                <MenuItem value="2068">消費者成功取件(退貨)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {/* ✅ New: Show Completed Returns Toggle */}
          <Grid item xs={6} sm={3} md={2}>
            <Button
              fullWidth
              size="small"
              variant={filters.includeCompleted ? "contained" : "outlined"}
              color={filters.includeCompleted ? "success" : "inherit"}
              startIcon={filters.includeCompleted ? <CheckCircle /> : <Visibility />}
              onClick={() => setFilters(prev => ({ 
                ...prev, 
                includeCompleted: !prev.includeCompleted 
              }))}
            >
              {filters.includeCompleted ? '顯示已完成' : '隱藏已完成'}
            </Button>
          </Grid>
          <Grid item xs={12} sm={12} md={3} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchRequests}
              disabled={loading}
            >
              重新整理
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      {/* Batch selection bar — only appears once something is ticked */}
      {selectedIds.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'action.hover' }}>
          <Typography variant="body2" fontWeight="bold">
            已選 {selectedIds.length} 筆退貨申請
          </Typography>
          <Button variant="contained" size="small" onClick={openBatchDialog}>
            批次退款
          </Button>
          <Button size="small" onClick={() => setSelectedIds([])}>
            清除選取
          </Button>
          <Typography variant="caption" color="text.secondary">
            金額由系統計算，按下按鈕前不會動到任何款項
          </Typography>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allBatchableSelected}
                  indeterminate={!allBatchableSelected && batchableOnPage.some(r => selectedIds.includes(r.request_id))}
                  disabled={batchableOnPage.length === 0}
                  onChange={toggleSelectAllOnPage}
                />
              </TableCell>
              <TableCell width={50}></TableCell>
              <TableCell>申請編號</TableCell>
              <TableCell>訂單編號</TableCell>
              <TableCell>類型</TableCell>
              <TableCell>客戶</TableCell>
              <TableCell>狀態</TableCell>
              <TableCell>寄件編號</TableCell>
              <TableCell>物流狀態</TableCell>
              <TableCell>申請時間</TableCell>
              <TableCell>完成時間</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                  <Typography variant="body2" color="text.secondary">
                    沒有找到申請記錄
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              requests.map((request) => (
                <React.Fragment key={request.id}>
                  <TableRow
                    hover
                    id={`err-row-${request.id}`}
                    sx={highlightedRowId === request.id ? {
                      transition: 'background-color 0.3s',
                      backgroundColor: 'action.selected',
                    } : undefined}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(request.request_id)}
                        disabled={!isBatchable(request)}
                        onChange={() => toggleSelected(request.request_id)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => setExpandedRow(expandedRow === request.id ? null : request.id)}
                      >
                        {expandedRow === request.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight="medium">
                        {request.request_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {request.order_source === 'storefront' ? request.original_order_number : `#${request.original_order_number}`}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                        {request.order_source === 'storefront' && (
                          <Chip label="Storefront" size="small" variant="outlined" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                        {request.refund_method === 'store_credit' && (
                          <Tooltip title={request.credit_granted_at
                            ? `已核發 NT$${(request.credit_granted_amount || 0).toLocaleString()} 購物金`
                            : '客戶選擇退款轉購物金，待核發'}>
                            <Chip
                              label={request.credit_granted_at ? '購物金 ✓' : '購物金'}
                              size="small"
                              color={request.credit_granted_at ? 'success' : 'warning'}
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          </Tooltip>
                        )}
                        {request.request_group_id && (
                          <Tooltip title={
                            getGroupSiblings(request).length > 0
                              ? '此申請與同一筆訂單的另一筆退／換貨申請合併送出，點擊跳至另一筆'
                              : '此申請為合併申請的一部分（另一筆不在目前清單中）'
                          }>
                            <Chip
                              label="合併申請"
                              size="small"
                              color="primary"
                              variant="outlined"
                              icon={<SwapHoriz sx={{ fontSize: '0.8rem' }} />}
                              onClick={() => jumpToSibling(request)}
                              sx={{ height: 18, fontSize: '0.65rem', cursor: 'pointer' }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getTypeLabel(request.request_type)}
                        color={getTypeColor(request.request_type)}
                        size="small"
                        icon={request.request_type === 'exchange' ? <SwapHoriz /> : <Undo />}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {request.customer_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {request.customer_email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(request.status, request.request_type, request.exchange_fulfillment_status)}
                        color={getStatusColorWithFulfillment(request.status, request.request_type, request.exchange_fulfillment_status)}
                        size="small"
                      />
                      {/* HK/MO: unpaid fee = stalled (do NOT ship); paid but no SF waybill = pickup never booked.
                          A standalone 退貨 never pays up front (fee comes off the refund), so it skips the fee gate. */}
                      {request.is_hk && !request.shipping_fee_paid && !isSfFeeDeductedFromRefund(request) && request.status !== 'cancelled' && (
                        <Tooltip title={`海外運費 NT$${request.return_shipping_fee || 100} 未付款 — 收到款項前不出貨`}>
                          <Chip label="⚠ 未付運費" color="error" size="small" sx={{ ml: 0.5 }} />
                        </Tooltip>
                      )}
                      {request.is_hk && (request.shipping_fee_paid || isSfFeeDeductedFromRefund(request)) && !request.sf_waybill_no && request.status !== 'cancelled' && request.status !== 'completed' && (
                        <Tooltip title={`順豐取件單建立失敗，需重新產生${request.sf_last_error ? `：${request.sf_last_error}` : ''}`}>
                          <Chip label="⚠ 順豐未建單" color="error" size="small" sx={{ ml: 0.5 }} />
                        </Tooltip>
                      )}
                      {/* Exchange inbound leg: without it the customer has no way to send
                          the original garment back. Clickable — books it on demand. */}
                      {request.is_hk && request.request_type === 'exchange' && request.shipping_fee_paid
                        && !request.sf_inbound_waybill_no && !request.request_group_id
                        && request.status !== 'cancelled' && request.status !== 'completed' && (
                        <Tooltip title="客戶沒有寄回原商品的順豐單，點擊建立回程單（香港→台灣）">
                          <Chip
                            label="⚠ 回程未建單"
                            color="error"
                            size="small"
                            sx={{ ml: 0.5, cursor: 'pointer' }}
                            onClick={() => handleBookExchangeInbound(request.request_id)}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {request.return_tracking_number ? (
                        <Tooltip title="點擊查看物流詳情">
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            sx={{ cursor: 'pointer', color: 'primary.main' }}
                          >
                            {request.return_tracking_number}
                          </Typography>
                        </Tooltip>
                      ) : getSfSendBackWaybill(request) ? (
                        <Tooltip title="客戶寄回的順豐單號 — 點擊到順豐官網查件">
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            component="a"
                            href={sfTrackingUrl(getSfSendBackWaybill(request)!)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            sx={{ color: 'primary.main', textDecoration: 'none', display: 'block' }}
                          >
                            {getSfSendBackWaybill(request)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {request.return_logistics_id ? (
                        <Chip
                          label={request.logistics_status ? getLogisticsStatusLabel(request.logistics_status, request.logistics_status_name) : '還未寄件'}
                          color={request.logistics_status ? getLogisticsStatusColor(request.logistics_status) : 'default'}
                          size="small"
                        />
                      ) : getSfSendBackWaybill(request) ? (
                        <Tooltip title="順豐上門取件單已建立。系統無法自動追蹤順豐狀態，請點擊到順豐官網查件確認客戶是否已寄出">
                          <Chip
                            label="順豐查件 ↗"
                            color="info"
                            size="small"
                            variant="outlined"
                            component="a"
                            href={sfTrackingUrl(getSfSendBackWaybill(request)!)}
                            target="_blank"
                            rel="noopener noreferrer"
                            clickable
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          />
                        </Tooltip>
                      ) : request.request_type === 'exchange' && request.exchange_fulfillment_status === 'fulfilled' ? (
                        <Tooltip title="換貨已出貨，客戶尚未寄回">
                          <Chip label="⚠ 待退回" color="warning" size="small" />
                        </Tooltip>
                      ) : request.request_type === 'exchange' && request.new_order_id ? (
                        <Chip
                          label={request.exchange_fulfillment_status === 'fulfilled' ? '已發貨' : '未出貨'}
                          color={request.exchange_fulfillment_status === 'fulfilled' ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(request.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {request.completed_at ? (
                        <Typography variant="body2" color="success.main" fontWeight="medium">
                          {formatDate(request.completed_at)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="查看詳情">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(request)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="更新狀態">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => openUpdateStatusDialog(request)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Row */}
                  <TableRow>
                    <TableCell sx={{ py: 0 }} colSpan={12}>
                      <Collapse in={expandedRow === request.id} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Grid container spacing={2}>
                            {/* Basic Info */}
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                基本資訊
                              </Typography>
                              <Stack spacing={1}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Phone fontSize="small" color="action" />
                                  <Typography variant="body2">{request.customer_phone}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Email fontSize="small" color="action" />
                                  <Typography variant="body2">{request.customer_email}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Receipt fontSize="small" color="action" />
                                  <Typography variant="body2">原因: {request.reason}</Typography>
                                </Box>
                              </Stack>
                            </Grid>

                            {/* Logistics Info — both returns and exchanges */}
                            {request.return_tracking_number && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                  物流資訊
                                </Typography>
                                <Stack spacing={1}>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <LocalShipping fontSize="small" color="action" />
                                    <Typography variant="body2">
                                      追蹤號: {request.return_tracking_number}
                                    </Typography>
                                  </Box>
                                  {request.return_store_name && (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Store fontSize="small" color="action" />
                                      <Typography variant="body2">
                                        門市: {request.return_store_name} ({request.return_store_id})
                                      </Typography>
                                    </Box>
                                  )}
                                  {request.return_logistics_id && (
                                    <Typography variant="caption" color="text.secondary">
                                      物流ID: {request.return_logistics_id}
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Chip
                                      label={request.logistics_status ? getLogisticsStatusLabel(request.logistics_status, request.logistics_status_name) : '還未寄件'}
                                      color={request.logistics_status ? getLogisticsStatusColor(request.logistics_status) : 'default'}
                                      size="small"
                                    />
                                    {request.return_logistics_id && (
                                      <Button
                                        size="small"
                                        variant="text"
                                        startIcon={<Refresh />}
                                        onClick={() => handleQueryLogisticsStatus(request.request_id)}
                                        sx={{ ml: 1 }}
                                      >
                                        查詢
                                      </Button>
                                    )}
                                  </Box>
                                </Stack>
                              </Grid>
                            )}

                            {/* Bank Info */}
                            {request.request_type === 'return' && (request.bank_code || request.bank_account) && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                  退款資訊
                                </Typography>
                                <Stack spacing={1}>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <AccountBalance fontSize="small" color="action" />
                                    <Typography variant="body2">
                                      銀行代號: {request.bank_code || '-'}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" sx={{ pl: 3 }}>
                                    帳號: {request.bank_account || '-'}
                                  </Typography>
                                </Stack>
                              </Grid>
                            )}

                            {/* Exchange Info */}
                            {request.request_type === 'exchange' && request.new_order_id && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                  換貨資訊
                                </Typography>
                                <Stack spacing={0.5}>
                                  <Typography variant="body2">
                                    新訂單號: #{request.new_order_id}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Typography variant="body2">換貨訂單:</Typography>
                                    <Chip
                                      label={request.exchange_fulfillment_status === 'fulfilled' ? '已發貨' : request.exchange_fulfillment_status === 'partial' ? '部分出貨' : '未出貨'}
                                      color={request.exchange_fulfillment_status === 'fulfilled' ? 'success' : 'default'}
                                      size="small"
                                    />
                                  </Box>
                                </Stack>
                                {/* Alert: we shipped exchange but customer hasn't sent return */}
                                {request.exchange_fulfillment_status === 'fulfilled' &&
                                  request.status !== 'completed' &&
                                  (!request.logistics_status || request.logistics_status === '7013' || request.logistics_status === '300') &&
                                  !['2067', '2068', '3022'].includes(request.logistics_status || '') && (
                                  <Alert severity="warning" sx={{ mt: 1 }} variant="outlined">
                                    換貨已出貨，但客戶尚未寄回退貨商品
                                  </Alert>
                                )}
                              </Grid>
                            )}

                            {/* Items */}
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                商品明細
                              </Typography>
                              <List dense>
                                {request.original_items?.map((item: any, index: number) => (
                                  <ListItem key={index} sx={{ bgcolor: 'white', mb: 0.5, borderRadius: 1, gap: 1.5 }}>
                                    {(item.imageUrl || item.image_url) ? (
                                      <Box
                                        component="img"
                                        src={item.imageUrl || item.image_url}
                                        sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                                      />
                                    ) : (
                                      <Box sx={{ width: 48, height: 48, bgcolor: 'grey.200', borderRadius: 1, flexShrink: 0 }} />
                                    )}
                                    <ListItemText
                                      primary={item.productName}
                                      secondary={`款式: ${item.variantTitle || 'Default'} | 數量: ${item.quantity}${item.price ? ` | NT$${parseFloat(item.price).toLocaleString()}` : ''}`}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                              {request.original_items?.length > 0 && (
                                <Typography variant="body2" fontWeight="bold" sx={{ mt: 1, textAlign: 'right' }}>
                                  退貨商品金額: NT${calculateRefundAmount(request.original_items).toLocaleString()}
                                </Typography>
                              )}
                            </Grid>

                            {/* Notes */}
                            {(request.customer_notes || request.admin_notes) && (
                              <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                  備註
                                </Typography>
                                {request.customer_notes && (
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>客戶:</strong> {request.customer_notes}
                                  </Typography>
                                )}
                                {request.admin_notes && (
                                  <Typography variant="body2" color="primary">
                                    <strong>管理員:</strong> {request.admin_notes}
                                  </Typography>
                                )}
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="每頁顯示:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} 共 ${count} 筆`}
        />
      </TableContainer>

      {/* ============ BATCH REFUND DIALOG ============ */}
      <Dialog open={batchOpen} onClose={() => !batchRunning && setBatchOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>批次退款 — {selectedIds.length} 筆</DialogTitle>
        <DialogContent dividers>
          {batchLoading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                正在依訂單實付金額計算每一筆應退金額…
              </Typography>
            </Box>
          ) : (
            <>
              {batchTotals && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <Chip color="primary" label={`信用卡 ${batchTotals.card.count} 筆 · NT$${Number(batchTotals.card.amount).toLocaleString()}`} />
                  <Chip color="success" label={`LINE Pay ${batchTotals.linepay.count} 筆 · NT$${Number(batchTotals.linepay.amount).toLocaleString()}`} />
                  <Chip color="secondary" label={`購物金 ${batchTotals.store_credit.count} 筆 · NT$${Number(batchTotals.store_credit.amount).toLocaleString()}`} />
                  <Chip color="warning" label={`匯款 ${batchTotals.manual.count} 筆 · NT$${Number(batchTotals.manual.amount).toLocaleString()}`} />
                  {batchTotals.blocked > 0 && <Chip color="error" label={`無法處理 ${batchTotals.blocked} 筆`} />}
                  {batchTotals.creditRestore > 0 && (
                    <Chip variant="outlined" label={`另回補購物金 NT$${Number(batchTotals.creditRestore).toLocaleString()}`} />
                  )}
                </Stack>
              )}

              <Alert severity="info" sx={{ mb: 2 }}>
                金額 = 退貨商品的<strong>實付</strong>金額（已攤分促銷/會員折扣、排除斷貨商品），
                現金退刷只退<strong>現金支付的部分</strong>；以購物金支付的部分會另外回補為購物金，
                不會重複退兩次。海外退貨已扣除 NT$100 回寄運費。
              </Alert>

              <FormControlLabel
                sx={{ mb: 1 }}
                control={
                  <Checkbox
                    size="small"
                    checked={batchRestorePromo}
                    disabled={batchRunning || batchDone}
                    onChange={async (e) => {
                      setBatchRestorePromo(e.target.checked);
                      await loadBatchPreview(e.target.checked);
                    }}
                  />
                }
                label="歸還促銷購物金（賣家責任退貨才勾）"
              />

              {batchRunning && <LinearProgress sx={{ mb: 1 }} />}

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>申請 / 訂單</TableCell>
                      <TableCell>客戶</TableCell>
                      <TableCell>方式</TableCell>
                      <TableCell align="right">金額</TableCell>
                      <TableCell>備註</TableCell>
                      <TableCell>結果</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batchPlans.map((plan) => {
                      const result = batchResults[plan.requestId];
                      const amount = plan.lane === 'store_credit' ? plan.creditGrant : plan.cashRefund;
                      return (
                        <TableRow
                          key={plan.requestId}
                          sx={{
                            opacity: plan.eligible ? 1 : 0.6,
                            bgcolor: batchCurrent === plan.requestId ? 'action.hover' : undefined,
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">{plan.orderId || '-'}</Typography>
                            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                              {plan.requestId}
                            </Typography>
                          </TableCell>
                          <TableCell>{plan.customerName || '-'}</TableCell>
                          <TableCell>
                            {plan.eligible
                              ? <Chip size="small" label={LANE_LABEL[plan.lane || ''] || plan.lane} />
                              : <Chip size="small" color="error" variant="outlined" label="無法處理" />}
                          </TableCell>
                          <TableCell align="right">
                            {plan.eligible ? (
                              <Typography variant="body2" fontWeight="bold">
                                NT${Number(amount).toLocaleString()}
                              </Typography>
                            ) : '-'}
                            {plan.eligible && plan.creditRestore > 0 && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                +購物金 {Number(plan.creditRestore).toLocaleString()}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {plan.blockedReason && (
                              <Typography variant="caption" color="error.main">{plan.blockedReason}</Typography>
                            )}
                            {plan.warnings?.map((w, i) => (
                              <Typography key={i} variant="caption" color="warning.main" display="block">{w}</Typography>
                            ))}
                            {plan.lane === 'manual' && plan.bank?.account && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {plan.bank.code} / {plan.bank.account}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {batchCurrent === plan.requestId && <CircularProgress size={14} />}
                            {result && (
                              <Typography variant="caption" color={result.ok ? 'success.main' : 'error.main'}>
                                {result.message}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {blockedPlans.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  有 {blockedPlans.length} 筆無法自動處理，會被跳過（原因見表格）。
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setBatchOpen(false)} disabled={batchRunning}>關閉</Button>
          <Box sx={{ flex: 1 }} />
          {manualPlans.length > 0 && (
            <>
              <Button onClick={copyRemittanceList} disabled={batchRunning}>
                複製匯款清單 ({manualPlans.length})
              </Button>
              <Button onClick={downloadBankFile} disabled={batchRunning || bankFileLoading}>
                {bankFileLoading ? '產生中…' : `下載上海銀行整批檔 (${manualPlans.length})`}
              </Button>
              <Button
                color="warning"
                variant="outlined"
                onClick={runBatchMarkRefunded}
                disabled={batchRunning}
              >
                標記退款完成 ({manualPlans.length})
              </Button>
            </>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={runBatchRefund}
            disabled={batchRunning || batchLoading || gatewayPlans.length === 0}
          >
            {batchRunning
              ? '處理中…'
              : `執行退刷／核發 ${gatewayPlans.length} 筆 · NT$${gatewayPlans
                  .reduce((t, p) => t + (p.lane === 'store_credit' ? p.creditGrant : p.cashRefund), 0)
                  .toLocaleString()}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          申請詳情
          {selectedRequest && (
            <Chip
              label={getTypeLabel(selectedRequest.request_type)}
              color={getTypeColor(selectedRequest.request_type)}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Grid container spacing={3}>
              {/* Request Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  申請資訊
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ border: 0, width: '30%' }}>申請編號</TableCell>
                      <TableCell sx={{ border: 0, fontFamily: 'monospace' }}>
                        {selectedRequest.request_id}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 0 }}>原訂單編號</TableCell>
                      <TableCell sx={{ border: 0 }}>
                        {selectedRequest.order_source === 'storefront' ? selectedRequest.original_order_number : `#${selectedRequest.original_order_number}`}
                        {selectedRequest.order_source === 'storefront' && (
                          <Chip label="Storefront" size="small" variant="outlined" color="info" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 0 }}>狀態</TableCell>
                      <TableCell sx={{ border: 0 }}>
                        <Chip
                          label={getStatusLabel(selectedRequest.status, selectedRequest.request_type, selectedRequest.exchange_fulfillment_status)}
                          color={getStatusColorWithFulfillment(selectedRequest.status, selectedRequest.request_type, selectedRequest.exchange_fulfillment_status)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 0 }}>申請時間</TableCell>
                      <TableCell sx={{ border: 0 }}>{formatDate(selectedRequest.created_at)}</TableCell>
                    </TableRow>
                    {selectedRequest.completed_at && (
                      <TableRow>
                        <TableCell sx={{ border: 0 }}>完成時間</TableCell>
                        <TableCell sx={{ border: 0 }}>{formatDate(selectedRequest.completed_at)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Customer Info */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  客戶資訊
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ border: 0, width: '40%' }}>姓名</TableCell>
                      <TableCell sx={{ border: 0 }}>{selectedRequest.customer_name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 0 }}>電話</TableCell>
                      <TableCell sx={{ border: 0 }}>{selectedRequest.customer_phone}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 0 }}>Email</TableCell>
                      <TableCell sx={{ border: 0 }}>{selectedRequest.customer_email}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>

              {/* Logistics/Bank Info */}
              <Grid item xs={12} md={6}>
                {selectedRequest.request_type === 'return' ? (
                  <>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                      退貨資訊
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        {selectedRequest.return_tracking_number && (
                          <TableRow>
                            <TableCell sx={{ border: 0, width: '40%' }}>追蹤號</TableCell>
                            <TableCell sx={{ border: 0, fontFamily: 'monospace' }}>
                              {selectedRequest.return_tracking_number}
                            </TableCell>
                          </TableRow>
                        )}
                        {selectedRequest.return_store_name && (
                          <TableRow>
                            <TableCell sx={{ border: 0 }}>退貨門市</TableCell>
                            <TableCell sx={{ border: 0 }}>
                              {selectedRequest.return_store_name}<br />
                              <Typography variant="caption" color="text.secondary">
                                ({selectedRequest.return_store_id})
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {getSfSendBackWaybill(selectedRequest) && (
                          <TableRow>
                            <TableCell sx={{ border: 0 }}>順豐寄回單號</TableCell>
                            <TableCell sx={{ border: 0, fontFamily: 'monospace' }}>
                              {getSfSendBackWaybill(selectedRequest)}
                              {getSfSendBackLabel(selectedRequest) && (
                                <Button size="small" href={getSfSendBackLabel(selectedRequest)!} target="_blank" rel="noopener noreferrer" sx={{ ml: 1 }}>
                                  寄件單
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell sx={{ border: 0 }}>物流狀態</TableCell>
                          <TableCell sx={{ border: 0 }}>
                            {!selectedRequest.return_logistics_id && getSfSendBackWaybill(selectedRequest) ? (
                              <Chip
                                label="順豐查件 ↗"
                                color="info"
                                size="small"
                                variant="outlined"
                                component="a"
                                href={sfTrackingUrl(getSfSendBackWaybill(selectedRequest)!)}
                                target="_blank"
                                rel="noopener noreferrer"
                                clickable
                              />
                            ) : (
                            <Chip
                              label={selectedRequest.logistics_status ? getLogisticsStatusLabel(selectedRequest.logistics_status, selectedRequest.logistics_status_name) : '還未寄件'}
                              color={selectedRequest.logistics_status ? getLogisticsStatusColor(selectedRequest.logistics_status) : 'default'}
                              size="small"
                            />
                            )}
                            {selectedRequest.logistics_status_updated_at && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {formatDate(selectedRequest.logistics_status_updated_at)}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                        {selectedRequest.return_logistics_id && (
                          <TableRow>
                            <TableCell sx={{ border: 0 }}></TableCell>
                            <TableCell sx={{ border: 0 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Refresh />}
                                onClick={() => handleQueryLogisticsStatus(selectedRequest.request_id)}
                              >
                                查詢物流狀態
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                        {Number(selectedRequest.storefront_store_credit_used || 0) > 0 && (
                          <TableRow>
                            <TableCell sx={{ border: 0 }}>原訂單付款組成</TableCell>
                            <TableCell sx={{ border: 0 }}>
                              <Typography variant="body2">
                                現金 NT${Number(selectedRequest.storefront_total_amount || 0).toLocaleString()}
                                {' ＋ '}購物金折抵 NT${Number(selectedRequest.storefront_store_credit_used || 0).toLocaleString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                購物金折抵部分退款不計回饋
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {selectedRequest.refund_method === 'store_credit' ? (
                          <TableRow>
                            <TableCell sx={{ border: 0 }}>退款方式</TableCell>
                            <TableCell sx={{ border: 0 }}>
                              <Chip
                                label={`購物金 +${Number(selectedRequest.credit_bonus_pct || 0)}%`}
                                size="small"
                                color="success"
                              />
                              {selectedRequest.credit_granted_at ? (
                                <Typography variant="caption" color="success.dark" sx={{ ml: 1 }}>
                                  已核發 NT${(selectedRequest.credit_granted_amount || 0).toLocaleString()}
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  待核發
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ) : (selectedRequest.bank_code || selectedRequest.bank_account) ? (
                          <>
                            <TableRow>
                              <TableCell sx={{ border: 0 }}>銀行代號</TableCell>
                              <TableCell sx={{ border: 0 }}>{selectedRequest.bank_code || '-'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ border: 0 }}>銀行帳號</TableCell>
                              <TableCell sx={{ border: 0, fontFamily: 'monospace' }}>
                                {selectedRequest.bank_account || '-'}
                              </TableCell>
                            </TableRow>
                          </>
                        ) : (() => {
                          const m = (selectedRequest.storefront_payment_method || '').toLowerCase();
                          const isLinePay = m === 'line_pay' || m === 'linepay';
                          return (
                            <TableRow>
                              <TableCell sx={{ border: 0 }}>退款方式</TableCell>
                              <TableCell sx={{ border: 0 }}>
                                <Chip
                                  label={isLinePay ? 'LINE Pay 退刷' : '信用卡退刷'}
                                  size="small"
                                  color="info"
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })()}
                      </TableBody>
                    </Table>
                  </>
                ) : (
                  <>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                      換貨資訊
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        {selectedRequest.new_order_id && (
                          <TableRow>
                            <TableCell sx={{ border: 0, width: '40%' }}>新訂單號</TableCell>
                            <TableCell sx={{ border: 0 }}>#{selectedRequest.new_order_id}</TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell sx={{ border: 0 }}>換貨訂單出貨</TableCell>
                          <TableCell sx={{ border: 0 }}>
                            <Chip
                              label={selectedRequest.exchange_fulfillment_status === 'fulfilled' ? '已發貨' : selectedRequest.exchange_fulfillment_status === 'partial' ? '部分出貨' : '未出貨'}
                              color={selectedRequest.exchange_fulfillment_status === 'fulfilled' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                        {selectedRequest.exchange_fulfillment_status === 'fulfilled' &&
                          selectedRequest.status !== 'completed' &&
                          (!selectedRequest.logistics_status || selectedRequest.logistics_status === '7013' || selectedRequest.logistics_status === '300') &&
                          !['2067', '2068', '3022'].includes(selectedRequest.logistics_status || '') && (
                          <TableRow>
                            <TableCell colSpan={2} sx={{ border: 0, pt: 0 }}>
                              <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                                換貨已出貨，但客戶尚未寄回退貨商品
                              </Alert>
                            </TableCell>
                          </TableRow>
                        )}
                        {selectedRequest.return_tracking_number && (
                          <TableRow>
                            <TableCell sx={{ border: 0 }}>退回追蹤號</TableCell>
                            <TableCell sx={{ border: 0, fontFamily: 'monospace' }}>
                              {selectedRequest.return_tracking_number}
                            </TableCell>
                          </TableRow>
                        )}
                        {selectedRequest.return_store_name && (
                          <TableRow>
                            <TableCell sx={{ border: 0 }}>退貨門市</TableCell>
                            <TableCell sx={{ border: 0 }}>
                              {selectedRequest.return_store_name}<br />
                              <Typography variant="caption" color="text.secondary">
                                ({selectedRequest.return_store_id})
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {getSfSendBackWaybill(selectedRequest) && (
                          <TableRow>
                            <TableCell sx={{ border: 0 }}>順豐寄回單號</TableCell>
                            <TableCell sx={{ border: 0, fontFamily: 'monospace' }}>
                              {getSfSendBackWaybill(selectedRequest)}
                              {getSfSendBackLabel(selectedRequest) && (
                                <Button size="small" href={getSfSendBackLabel(selectedRequest)!} target="_blank" rel="noopener noreferrer" sx={{ ml: 1 }}>
                                  寄件單
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell sx={{ border: 0 }}>物流狀態</TableCell>
                          <TableCell sx={{ border: 0 }}>
                            {!selectedRequest.return_logistics_id && getSfSendBackWaybill(selectedRequest) ? (
                              <Chip
                                label="順豐查件 ↗"
                                color="info"
                                size="small"
                                variant="outlined"
                                component="a"
                                href={sfTrackingUrl(getSfSendBackWaybill(selectedRequest)!)}
                                target="_blank"
                                rel="noopener noreferrer"
                                clickable
                              />
                            ) : (
                            <Chip
                              label={selectedRequest.logistics_status ? getLogisticsStatusLabel(selectedRequest.logistics_status, selectedRequest.logistics_status_name) : '還未寄件'}
                              color={selectedRequest.logistics_status ? getLogisticsStatusColor(selectedRequest.logistics_status) : 'default'}
                              size="small"
                            />
                            )}
                            {selectedRequest.logistics_status_updated_at && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {formatDate(selectedRequest.logistics_status_updated_at)}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                        {selectedRequest.return_logistics_id && (
                          <TableRow>
                            <TableCell sx={{ border: 0 }}></TableCell>
                            <TableCell sx={{ border: 0 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Refresh />}
                                onClick={() => handleQueryLogisticsStatus(selectedRequest.request_id)}
                              >
                                查詢物流狀態
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </>
                )}
              </Grid>

              {/* Refund history (per-event 退刷 log) */}
              {Array.isArray(selectedRequest.refund_history) && selectedRequest.refund_history.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    退刷紀錄
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: '32%' }}>時間</TableCell>
                        <TableCell>方式</TableCell>
                        <TableCell align="right">金額</TableCell>
                        <TableCell align="right">累計 / 原金額</TableCell>
                        <TableCell>操作者</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedRequest.refund_history.map((r) => {
                        const amt = parseFloat(r.amount as any) || 0;
                        const cum = r.cumulative_refunded != null ? parseFloat(r.cumulative_refunded as any) : null;
                        const orig = r.original_amount != null ? parseFloat(r.original_amount as any) : null;
                        const methodLabel = r.payment_method === 'linepay'
                          ? 'LINE Pay'
                          : r.payment_method === 'ecpg'
                          ? '信用卡'
                          : r.payment_method;
                        return (
                          <TableRow key={r.id}>
                            <TableCell>{formatDate(r.created_at)}</TableCell>
                            <TableCell>
                              <Chip label={methodLabel} size="small" variant="outlined" color="info" />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold" color="error.main">
                                NT${Math.round(amt).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {cum != null && orig != null
                                ? `NT$${Math.round(cum).toLocaleString()} / NT$${Math.round(orig).toLocaleString()}`
                                : '-'}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {r.created_by || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell sx={{ borderBottom: 0 }} colSpan={2}>
                          <Typography variant="caption" color="text.secondary">
                            合計已退刷
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: 0 }}>
                          <Typography variant="body2" fontWeight="bold" color="error.main">
                            NT${selectedRequest.refund_history
                              .reduce((sum, r) => sum + (parseFloat(r.amount as any) || 0), 0)
                              .toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell colSpan={2} sx={{ borderBottom: 0 }} />
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Original Items */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {selectedRequest.request_type === 'exchange' ? '原商品' : '退貨商品'}
                  </Typography>
                  {selectedRequest.order_source === 'storefront' &&
                    (selectedRequest.original_items?.length || 0) > 1 && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<AddShoppingCart />}
                        onClick={() => handleOpenSfBatchOutlet(selectedRequest)}
                      >
                        批次加入 Outlet ({selectedRequest.original_items?.length})
                      </Button>
                    )}
                </Box>
                <List>
                  {selectedRequest.original_items?.map((item: any, index: number) => (
                    <ListItem
                      key={index}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        gap: 1.5
                      }}
                    >
                      {(item.imageUrl || item.image_url) ? (
                        <Box
                          component="img"
                          src={item.imageUrl || item.image_url}
                          sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                        />
                      ) : (
                        <Box sx={{ width: 56, height: 56, bgcolor: 'grey.200', borderRadius: 1, flexShrink: 0 }} />
                      )}
                      <ListItemText
                        primary={item.productName}
                        secondary={(() => {
                          const regular = parseFloat(item.price) || 0;
                          const paid = parseFloat(item.paidPrice);
                          const showPaid = Number.isFinite(paid) && paid > 0 && Math.abs(paid - regular) >= 1;
                          return (
                            <>
                              款式: {item.variantTitle || 'Default'}<br />
                              數量: {item.quantity}
                              {regular > 0 && !showPaid && (
                                <> | 單價: NT${Math.round(regular).toLocaleString()}</>
                              )}
                              {showPaid && (
                                <>
                                  {' | 單價: '}
                                  <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.disabled', mr: 0.5 }}>
                                    NT${Math.round(regular).toLocaleString()}
                                  </Box>
                                  <Box component="span" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                    實付 NT${Math.round(paid).toLocaleString()}
                                  </Box>
                                </>
                              )}
                            </>
                          );
                        })()}
                      />
                      {(() => {
                        const regular = parseFloat(item.price) || 0;
                        const paidLine = parseFloat(item.paidLineSubtotal);
                        const paidUnit = parseFloat(item.paidPrice);
                        const qty = parseInt(item.quantity) || 1;
                        const lineTotal = Number.isFinite(paidLine) && paidLine > 0
                          ? paidLine
                          : (Number.isFinite(paidUnit) && paidUnit > 0 ? paidUnit * qty : regular * qty);
                        const regularLine = regular * qty;
                        const showStrike = lineTotal > 0 && Math.abs(lineTotal - regularLine) >= 1;
                        if (lineTotal <= 0) return null;
                        return (
                          <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {showStrike && (
                              <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.disabled', display: 'block' }}>
                                NT${Math.round(regularLine).toLocaleString()}
                              </Typography>
                            )}
                            <Typography variant="body2" fontWeight="bold" color={showStrike ? 'error.main' : 'text.primary'}>
                              NT${Math.round(lineTotal).toLocaleString()}
                            </Typography>
                          </Box>
                        );
                      })()}
                      {selectedRequest.order_source === 'storefront' && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<AddShoppingCart />}
                          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                          onClick={() => handleOpenSfOutlet(item, selectedRequest)}
                        >
                          加入 Outlet
                        </Button>
                      )}
                    </ListItem>
                  ))}
                </List>
                {selectedRequest.original_items?.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="error.main">
                      退貨商品金額: NT${calculateRefundAmount(selectedRequest.original_items).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* New Items (for exchange) */}
              {selectedRequest.request_type === 'exchange' && selectedRequest.new_items && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    換貨商品
                  </Typography>
                  <List>
                    {selectedRequest.new_items.map((item: any, index: number) => (
                      <ListItem
                        key={index}
                        sx={{
                          border: 1,
                          borderColor: 'primary.main',
                          borderRadius: 1,
                          mb: 1,
                          bgcolor: 'primary.50',
                          gap: 1.5
                        }}
                      >
                        {(item.imageUrl || item.image_url) ? (
                          <Box
                            component="img"
                            src={item.imageUrl || item.image_url}
                            sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                          />
                        ) : (
                          <Box sx={{ width: 56, height: 56, bgcolor: 'grey.200', borderRadius: 1, flexShrink: 0 }} />
                        )}
                        <ListItemText
                          primary={item.productName || `款式 ID: ${item.variantId}`}
                          secondary={
                            <>
                              款式: {item.variantTitle}<br />
                              數量: {item.quantity}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              )}

              {/* Reason and Notes */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  原因與備註
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ border: 0, width: '20%', verticalAlign: 'top' }}>原因</TableCell>
                      <TableCell sx={{ border: 0 }}>{selectedRequest.reason}</TableCell>
                    </TableRow>
                    {selectedRequest.customer_notes && (
                      <TableRow>
                        <TableCell sx={{ border: 0, verticalAlign: 'top' }}>客戶備註</TableCell>
                        <TableCell sx={{ border: 0 }}>{selectedRequest.customer_notes}</TableCell>
                      </TableRow>
                    )}
                    {selectedRequest.admin_notes && (
                      <TableRow>
                        <TableCell sx={{ border: 0, verticalAlign: 'top' }}>管理員備註</TableCell>
                        <TableCell sx={{ border: 0, color: 'primary.main' }}>
                          {selectedRequest.admin_notes}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>關閉</Button>
          {/* Store-credit refund: shown for store_credit requests, disabled
              once the credit has been granted. Replaces the gateway-refund
              path entirely for these — they never get a card/LINE Pay refund. */}
          {selectedRequest?.request_type === 'return' &&
            selectedRequest?.refund_method === 'store_credit' &&
            (selectedRequest.credit_granted_at ? (
              <Chip
                label={`已核發 NT$${(selectedRequest.credit_granted_amount || 0).toLocaleString()} 購物金`}
                color="success"
                variant="outlined"
              />
            ) : (
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  setDetailsOpen(false);
                  openGrantCreditDialog(selectedRequest);
                }}
              >
                核發購物金
              </Button>
            ))}
          {/* Gateway refund — hidden when the customer chose store_credit. */}
          {selectedRequest?.request_type === 'return' &&
            selectedRequest?.refund_method !== 'store_credit' &&
            selectedRequest?.order_source === 'storefront' &&
            !selectedRequest?.bank_code &&
            !selectedRequest?.bank_account &&
            (() => {
              const method = (selectedRequest.storefront_payment_method || '').toLowerCase();
              const isLinePay = method === 'line_pay' || method === 'linepay';
              const isCard = method === 'credit_card' || method === 'ecpay' || method === 'ecpg' || method === 'card';
              if (!isLinePay && !isCard) return null;
              return (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => {
                    setDetailsOpen(false);
                    openRefundDialog(selectedRequest);
                  }}
                >
                  退刷 {isLinePay ? 'LINE Pay' : '信用卡'}
                </Button>
              );
            })()}
          <Button
            variant="contained"
            onClick={() => {
              setDetailsOpen(false);
              openUpdateStatusDialog(selectedRequest!);
            }}
            startIcon={<Edit />}
          >
            更新狀態
          </Button>
        </DialogActions>
      </Dialog>

      {/* Grant Store Credit Dialog */}
      <Dialog
        open={grantCreditDialogOpen}
        onClose={() => !grantCreditProcessing && setGrantCreditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>核發購物金</DialogTitle>
        <DialogContent>
          {grantCreditTarget && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning">
                請確認倉庫已收到所有退貨商品。核發後購物金立即存入客戶帳戶，操作不可逆。
              </Alert>
              <Box>
                <Typography variant="body2" color="text.secondary">申請單號</Typography>
                <Typography>{grantCreditTarget.request_id}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">訂單編號</Typography>
                <Typography>{grantCreditTarget.original_order_number}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">客戶</Typography>
                <Typography>
                  {grantCreditTarget.customer_name} ({grantCreditTarget.customer_email})
                </Typography>
              </Box>
              {grantCreditLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">計算購物金金額中...</Typography>
                </Box>
              )}
              {grantCreditProjection && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="body2" color="text.secondary">退貨商品實付小計</Typography>
                    <Typography>NT${grantCreditProjection.base.toLocaleString()}</Typography>
                    {grantCreditProjection.cashRatio < 1 && (() => {
                      const creditPart = Math.round(grantCreditProjection.base * (1 - grantCreditProjection.cashRatio));
                      const cashPart = grantCreditProjection.base - creditPart;
                      return (
                        <Typography variant="caption" color="text.secondary">
                          含現金 NT${cashPart.toLocaleString()} ‧ 購物金折抵 NT${creditPart.toLocaleString()}
                        </Typography>
                      );
                    })()}
                  </Box>
                  {/* Explicit origin split: cash (+bonus), real-money credit
                      (always restored), promo credit (forfeited unless ticked). */}
                  <Divider textAlign="left"><Typography variant="caption" color="text.secondary">退款組成</Typography></Divider>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      現金退還
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        （+{grantCreditProjection.effectiveBonusPct}% 回饋 NT${grantCreditProjection.bonus.toLocaleString()}）
                      </Typography>
                    </Typography>
                    <Typography variant="body2">NT${(grantCreditProjection.cashPortion + grantCreditProjection.bonus).toLocaleString()}</Typography>
                  </Box>
                  {grantCreditProjection.moneyCreditPortion > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">
                        實質購物金退還
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          （原為退款產生，永久有效）
                        </Typography>
                      </Typography>
                      <Typography variant="body2">NT${grantCreditProjection.moneyCreditPortion.toLocaleString()}</Typography>
                    </Box>
                  )}
                  {grantCreditProjection.promoCreditPortion > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color={grantRestorePromo ? 'text.primary' : 'text.disabled'}>
                        促銷購物金
                        <Typography component="span" variant="caption" sx={{ ml: 0.5 }}
                          color={grantRestorePromo ? 'warning.main' : 'text.disabled'}>
                          {grantRestorePromo ? '（將返還 · 90 天效期）' : '（不返還）'}
                        </Typography>
                      </Typography>
                      <Typography variant="body2"
                        color={grantRestorePromo ? 'text.primary' : 'text.disabled'}
                        sx={{ textDecoration: grantRestorePromo ? 'none' : 'line-through' }}>
                        NT${grantCreditProjection.promoCreditPortion.toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                  {grantCreditProjection.promoCreditPortion > 0 && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={grantRestorePromo}
                          onChange={(e) => setGrantRestorePromo(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          歸還促銷購物金（賣家原因退貨時勾選）
                        </Typography>
                      }
                    />
                  )}
                  <Box sx={{ p: 1.5, bgcolor: 'success.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">將存入客戶購物金</Typography>
                    <Typography variant="h6" color="success.dark" fontWeight={700}>
                      NT${(grantCreditProjection.totalCredit + (grantRestorePromo ? grantCreditProjection.promoCreditPortion : 0)).toLocaleString()}
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setGrantCreditDialogOpen(false)}
            disabled={grantCreditProcessing}
          >
            取消
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleGrantCredit}
            disabled={grantCreditProcessing || grantCreditLoading || !grantCreditProjection}
          >
            {grantCreditProcessing
              ? <CircularProgress size={20} />
              : `確認核發 NT$${grantCreditProjection
                  ? (grantCreditProjection.totalCredit + (grantRestorePromo ? grantCreditProjection.promoCreditPortion : 0)).toLocaleString()
                  : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog
        open={refundDialogOpen}
        onClose={() => !refundProcessing && setRefundDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>退刷確認</DialogTitle>
        <DialogContent>
          {refundTargetRequest && (() => {
            const method = (refundTargetRequest.storefront_payment_method || '').toLowerCase();
            const isLinePay = method === 'line_pay' || method === 'linepay';
            const label = isLinePay ? 'LINE Pay' : '信用卡 (ECPay)';
            const total = Number(refundTargetRequest.storefront_total_amount || 0);
            // Prefer paid-price totals over regular-price totals (paidLineSubtotal
            // is set by the backend admin/requests enrichment for storefront returns).
            const itemsTotal = calculateRefundAmount(refundTargetRequest.original_items || []);
            const regularTotal = Array.isArray(refundTargetRequest.original_items)
              ? refundTargetRequest.original_items.reduce(
                  (sum: number, it: any) =>
                    sum + Number(it?.price || 0) * Number(it?.quantity || 1),
                  0
                )
              : 0;
            const showStrikethroughRegular = regularTotal > 0 && Math.abs(regularTotal - itemsTotal) >= 1;
            return (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Alert severity="warning">
                  此操作會直接向金流端發送退刷請求，請確認金額正確後再執行。
                </Alert>
                <Box>
                  <Typography variant="body2" color="text.secondary">申請單號</Typography>
                  <Typography>{refundTargetRequest.request_id}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">訂單編號</Typography>
                  <Typography>{refundTargetRequest.original_order_number}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">付款方式</Typography>
                  <Typography>{label}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">訂單金額</Typography>
                  <Typography>NT${total.toLocaleString()}</Typography>
                </Box>
                {(() => {
                  const refunded = Number(refundTargetRequest.storefront_refunded_amount || 0);
                  const remaining = Number(refundTargetRequest.storefront_remaining_refundable);
                  if (!Number.isFinite(remaining)) return null;
                  if (refunded <= 0 && remaining >= Math.round(itemsTotal)) return null;
                  return (
                    <Box>
                      <Typography variant="body2" color="text.secondary">已退 / 可退餘額</Typography>
                      <Typography fontWeight="bold" color={remaining > 0 ? 'warning.main' : 'error.main'}>
                        已退 NT${refunded.toLocaleString()}
                        {' · '}可退餘額 NT${remaining.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {remaining <= 0
                          ? '此訂單金流端已無可退金額（已全額退刷，或以購物金／換貨單成立、無現金交易）。若仍需退款，請走購物金退還流程。'
                          : '此訂單先前已有退刷（例如斷貨退款），訂單金額不會因此減少，退刷金額不得超過可退餘額。'}
                      </Typography>
                    </Box>
                  );
                })()}
                {itemsTotal > 0 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">退貨商品小計</Typography>
                    {showStrikethroughRegular ? (
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
                          NT${Math.round(regularTotal).toLocaleString()}
                        </Typography>
                        <Typography fontWeight="bold" color="error.main">
                          實付 NT${Math.round(itemsTotal).toLocaleString()}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography>NT${Math.round(itemsTotal).toLocaleString()}</Typography>
                    )}
                  </Box>
                )}
                <TextField
                  label="退刷金額 (NT$)"
                  type="number"
                  fullWidth
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  helperText="預設為退貨商品小計，可依實際退款金額修改"
                />
                {Number(refundTargetRequest.storefront_store_credit_used || 0) > 0 && (
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      此訂單曾折抵購物金 NT${Number(refundTargetRequest.storefront_store_credit_used || 0).toLocaleString()}。
                      退刷僅退回現金部分；購物金部分將依來源自動退還（實質購物金永久有效，促銷購物金預設不返還）。
                    </Typography>
                    <FormControlLabel
                      sx={{ mt: 0.5 }}
                      control={
                        <Checkbox
                          checked={refundRestorePromo}
                          onChange={(e) => setRefundRestorePromo(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          歸還促銷購物金（賣家原因退貨時勾選 · 90 天效期）
                        </Typography>
                      }
                    />
                  </Box>
                )}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)} disabled={refundProcessing}>
            取消
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRefund}
            disabled={refundProcessing || !refundAmount}
          >
            {refundProcessing ? <CircularProgress size={20} /> : '確認退刷'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog
        open={updateStatusDialogOpen}
        onClose={() => setUpdateStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>更新申請狀態</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>狀態</InputLabel>
                <Select
                  value={newStatus}
                  label="狀態"
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <MenuItem value="pending">待處理</MenuItem>
                  <MenuItem value="approved">已批准</MenuItem>
                  <MenuItem value="processing">處理中</MenuItem>
                  <MenuItem value="completed">{getStatusLabel('completed', selectedRequest?.request_type)}</MenuItem>
                  <MenuItem value="cancelled">已取消</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="管理員備註"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="輸入處理備註..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateStatusDialogOpen(false)}>取消</Button>
          <Button variant="contained" onClick={handleUpdateStatus}>
            確認更新
          </Button>
        </DialogActions>
      </Dialog>

      </>)}

      {/* ============ TAB 1: SHOPEE RETURNS ============ */}
      {mainTab === 1 && (
        <>
          {/* Shopee Filters + Batch Action */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="搜尋商品名稱、SKU..."
                  value={shopeeSearch}
                  onChange={(e) => setShopeeSearch(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchShopeeReturns}
                  disabled={shopeeLoading}
                >
                  重新整理
                </Button>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <Typography variant="body2" color="text.secondary">
                  共 {outletReadyItems.length} 件可上架
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={5} sx={{ textAlign: 'right' }}>
                {selectedShopeeItems.size > 0 && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<AddShoppingCart />}
                    onClick={() => setBatchOutletDialog(true)}
                  >
                    批次加入 Outlet ({selectedShopeeItems.size})
                  </Button>
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* Flat Items Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={outletReadyItems.length > 0 && selectedShopeeItems.size === outletReadyItems.length}
                      indeterminate={selectedShopeeItems.size > 0 && selectedShopeeItems.size < outletReadyItems.length}
                      onChange={toggleSelectAll}
                    />
                  </TableCell>
                  <TableCell>圖片</TableCell>
                  <TableCell>商品名稱</TableCell>
                  <TableCell>顏色</TableCell>
                  <TableCell>尺寸</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>數量</TableCell>
                  <TableCell>單價</TableCell>
                  <TableCell>退貨原因</TableCell>
                  <TableCell>日期</TableCell>
                  <TableCell align="center">加入Outlet</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shopeeLoading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : outletReadyItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                      <Typography variant="body2" color="text.secondary">
                        沒有可加入 Outlet 的退貨商品
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  outletReadyItems.map((row) => {
                    const variant = getVariantInfo(row.item);
                    return (
                      <TableRow key={row.key} hover selected={selectedShopeeItems.has(row.key)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedShopeeItems.has(row.key)}
                            onChange={() => toggleSelectItem(row.key)}
                          />
                        </TableCell>
                        <TableCell>
                          {row.item.images?.[0] ? (
                            <Box
                              component="img"
                              src={row.item.images[0]}
                              sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }}
                            />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 250 }} noWrap>{row.item.name}</Typography>
                          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                            {row.orderSn}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{variant.color}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{variant.size}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">{row.item.item_sku || '-'}</Typography>
                        </TableCell>
                        <TableCell>{row.item.amount}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            TWD {row.item.item_price?.toFixed(0) || '0'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={row.textReason || row.reason}>
                            <Typography variant="caption" sx={{ maxWidth: 120 }} noWrap>
                              {row.textReason || row.reason}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(row.createTime * 1000).toLocaleDateString('zh-TW', {
                              month: '2-digit', day: '2-digit'
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<AddShoppingCart />}
                            onClick={() => handleOpenAddToOutlet(row.item, row.returnSn)}
                          >
                            加入
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Single Item Add to Outlet Dialog */}
          <Dialog
            open={addToOutletDialog.open}
            onClose={() => setAddToOutletDialog({ open: false, item: null, returnSn: '' })}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>加入 Outlet</DialogTitle>
            <DialogContent>
              {addToOutletDialog.item && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      {addToOutletDialog.item.images?.[0] && (
                        <Box
                          component="img"
                          src={addToOutletDialog.item.images[0]}
                          sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1 }}
                        />
                      )}
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">{addToOutletDialog.item.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          SKU: {addToOutletDialog.item.item_sku || '-'} | 款式: {addToOutletDialog.item.variation_sku || 'Default'}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          原價: TWD {addToOutletDialog.item.item_price?.toFixed(0)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Outlet 特價"
                      type="number"
                      value={outletDiscountPrice}
                      onChange={(e) => setOutletDiscountPrice(e.target.value)}
                      helperText={
                        outletDiscountPrice && addToOutletDialog.item.item_price
                          ? `折扣 ${Math.round(((addToOutletDialog.item.item_price - parseFloat(outletDiscountPrice)) / addToOutletDialog.item.item_price) * 100)}%`
                          : '必須低於原價'
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="數量"
                      type="number"
                      value={outletQuantity}
                      onChange={(e) => setOutletQuantity(parseInt(e.target.value) || 1)}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddToOutletDialog({ open: false, item: null, returnSn: '' })}>取消</Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleAddToOutlet}
                disabled={addingToOutlet || !outletDiscountPrice || parseFloat(outletDiscountPrice) <= 0}
                startIcon={addingToOutlet ? <CircularProgress size={16} /> : <AddShoppingCart />}
              >
                {addingToOutlet ? '處理中...' : '確認加入'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Batch Add to Outlet Dialog */}
          <Dialog
            open={batchOutletDialog}
            onClose={() => !batchAdding && setBatchOutletDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>批次加入 Outlet ({selectedShopeeItems.size} 件商品)</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  統一設定折扣百分比，系統會根據每件商品的原價自動計算特價。
                </Typography>
                <TextField
                  fullWidth
                  label="折扣百分比 (%)"
                  type="number"
                  value={batchDiscountPercent}
                  onChange={(e) => setBatchDiscountPercent(e.target.value)}
                  helperText="例如：20 表示打八折（原價的 80%）"
                  inputProps={{ min: 1, max: 99 }}
                />
                <Divider />
                <Typography variant="subtitle2">選取的商品：</Typography>
                {outletReadyItems
                  .filter(i => selectedShopeeItems.has(i.key))
                  .map(row => {
                    const variant = getVariantInfo(row.item);
                    const discountPrice = batchDiscountPercent
                      ? Math.round(row.item.item_price * (1 - parseFloat(batchDiscountPercent) / 100))
                      : 0;
                    return (
                      <Box key={row.key} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.5 }}>
                        {row.item.images?.[0] && (
                          <Box component="img" src={row.item.images[0]} sx={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 0.5 }} />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>{row.item.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {variant.color} / {variant.size} — SKU: {row.item.item_sku || '-'}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                            {row.item.item_price?.toFixed(0)}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            → TWD {discountPrice > 0 ? discountPrice : '?'}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setBatchOutletDialog(false)} disabled={batchAdding}>取消</Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleBatchAddToOutlet}
                disabled={batchAdding || !batchDiscountPercent}
                startIcon={batchAdding ? <CircularProgress size={16} /> : <AddShoppingCart />}
              >
                {batchAdding ? '處理中...' : `確認加入 ${selectedShopeeItems.size} 件`}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* ============ TAB 2: 未取貨 (CVS PICKUP EXPIRED) ============ */}
      {mainTab === 2 && (
        <>
          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    待處理
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {expiredStats.pending}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    已處理
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {expiredStats.resolved}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    總計
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {expiredStats.total}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="搜尋訂單編號、客戶、門市..."
                  value={expiredSearch}
                  onChange={(e) => setExpiredSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchExpiredPickups(); }}
                  InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>處理狀態</InputLabel>
                  <Select
                    value={expiredFilter}
                    label="處理狀態"
                    onChange={(e) => setExpiredFilter(e.target.value as 'pending' | 'resolved' | 'all')}
                  >
                    <MenuItem value="pending">待處理</MenuItem>
                    <MenuItem value="resolved">已處理</MenuItem>
                    <MenuItem value="all">全部</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchExpiredPickups}
                  disabled={expiredLoading}
                >
                  重新整理
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Info banner */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>取貨付款</strong>訂單已自動取消（包裹會退回，到貨後加入庫存或 Outlet）；
            <strong>信用卡 / LINE Pay</strong> 需主動聯繫客戶決定退款或重寄。
          </Alert>

          {/* Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>訂單編號</TableCell>
                  <TableCell>客戶</TableCell>
                  <TableCell>門市</TableCell>
                  <TableCell align="right">金額</TableCell>
                  <TableCell>付款方式</TableCell>
                  <TableCell>商品</TableCell>
                  <TableCell>未取貨日期</TableCell>
                  <TableCell>處理狀態</TableCell>
                  <TableCell>挽回</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expiredLoading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : expiredPickups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                      <Typography variant="body2" color="text.secondary">
                        {expiredFilter === 'pending' ? '目前沒有待處理的未取貨訂單 🎉' : '沒有符合條件的訂單'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  expiredPickups.map((order) => {
                    const cod = isCodOrder(order.payment_method);
                    return (
                      <TableRow key={order.order_id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{order.order_id}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatLogisticsSubType(order.logistics_sub_type)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{order.customer_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{order.customer_phone}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{order.store_name || '-'}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            NT${Number(order.total_amount).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={formatPaymentMethod(order.payment_method)}
                            color={cod ? 'default' : 'warning'}
                            variant={cod ? 'outlined' : 'filled'}
                          />
                          {!cod && (
                            <Typography variant="caption" display="block" color="warning.main" sx={{ mt: 0.5 }}>
                              已付款 — 需退款/重寄
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {(order.items || []).slice(0, 3).map((it, idx) => (
                              <Tooltip
                                key={idx}
                                title={
                                  <Box>
                                    <Typography variant="caption" component="div" fontWeight="bold">{it.title}</Typography>
                                    {it.variant_details && (
                                      <Typography variant="caption" component="div">{it.variant_details}</Typography>
                                    )}
                                    <Typography variant="caption" component="div">x{it.quantity}</Typography>
                                  </Box>
                                }
                              >
                                <Avatar
                                  src={it.image_url || undefined}
                                  variant="rounded"
                                  sx={{ width: 36, height: 36, bgcolor: 'grey.200' }}
                                >
                                  {!it.image_url && <Inventory2 fontSize="small" />}
                                </Avatar>
                              </Tooltip>
                            ))}
                            {(order.items || []).length > 3 && (
                              <Typography variant="caption" color="text.secondary">
                                +{(order.items || []).length - 3}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {order.logistics_updated_at ? formatDate(order.logistics_updated_at) : '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {logisticsStatusMap[order.logistics_status]?.label || `代碼 ${order.logistics_status}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={getResolutionLabel(order.pickup_expired_resolution)}
                            color={getResolutionColor(order.pickup_expired_resolution)}
                          />
                          {order.pickup_expired_admin_notes && (
                            <Tooltip title={order.pickup_expired_admin_notes}>
                              <IconButton size="small" sx={{ ml: 0.5 }}>
                                <Notes fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          {getRescueLabel(order) === '—' ? (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          ) : (
                            <Chip
                              size="small"
                              label={getRescueLabel(order)}
                              color={getRescueColor(order)}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="檢視詳情">
                              <IconButton
                                size="small"
                                onClick={() => { setExpiredDetailOrder(order); setExpiredDetailOpen(true); }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="挽回：建立新訂單">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => openRescueDialog(order)}
                              >
                                <Replay fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="標記處理">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openResolveDialog(order)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Detail Dialog */}
          <Dialog
            open={expiredDetailOpen}
            onClose={() => setExpiredDetailOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <EventBusy color="warning" />
                <span>未取貨訂單詳情 — {expiredDetailOrder?.order_id}</span>
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              {expiredDetailOrder && (
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">客戶</Typography>
                      <Typography variant="body1">{expiredDetailOrder.customer_name}</Typography>
                      <Typography variant="body2" color="text.secondary">{expiredDetailOrder.customer_phone}</Typography>
                      {expiredDetailOrder.customer_email && (
                        <Typography variant="body2" color="text.secondary">{expiredDetailOrder.customer_email}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">取貨門市</Typography>
                      <Typography variant="body1">{expiredDetailOrder.store_name || '-'}</Typography>
                      <Typography variant="body2" color="text.secondary">{expiredDetailOrder.store_address || ''}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatLogisticsSubType(expiredDetailOrder.logistics_sub_type)} · 店號 {expiredDetailOrder.store_id || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">付款方式</Typography>
                      <Typography variant="body1">{formatPaymentMethod(expiredDetailOrder.payment_method)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">訂單金額</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        NT${Number(expiredDetailOrder.total_amount).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">物流狀態</Typography>
                      <Typography variant="body1">
                        {logisticsStatusMap[expiredDetailOrder.logistics_status]?.label || expiredDetailOrder.logistics_status_msg || expiredDetailOrder.logistics_status}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        更新於 {expiredDetailOrder.logistics_updated_at ? formatDate(expiredDetailOrder.logistics_updated_at) : '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">下單時間</Typography>
                      <Typography variant="body1">{formatDate(expiredDetailOrder.created_at)}</Typography>
                    </Grid>
                  </Grid>

                  <Divider />
                  <Typography variant="subtitle1" fontWeight="bold">商品明細</Typography>
                  <List dense>
                    {(expiredDetailOrder.items || []).map((it) => (
                      <ListItem key={it.id} sx={{ alignItems: 'flex-start' }}>
                        <Avatar
                          src={it.image_url || undefined}
                          variant="rounded"
                          sx={{ width: 56, height: 56, mr: 2, bgcolor: 'grey.200' }}
                        >
                          {!it.image_url && <Inventory2 />}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight="bold">
                              {it.title}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              {it.variant_details && (
                                <Typography variant="caption" display="block">
                                  規格：{it.variant_details}
                                </Typography>
                              )}
                              {it.sku && (
                                <Typography variant="caption" display="block">SKU：{it.sku}</Typography>
                              )}
                              <Typography variant="caption" display="block">
                                數量 x{it.quantity} · 單價 NT${Number(it.discount_price || it.price).toLocaleString()}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>

                  {expiredDetailOrder.rescue_response_action === 'reship' && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          新收件資訊（客戶要求重寄）
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">收件人</Typography>
                            <Typography variant="body1">{expiredDetailOrder.rescue_new_receiver_name || '-'}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {expiredDetailOrder.rescue_new_receiver_phone || ''}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">新門市</Typography>
                            <Typography variant="body1">{expiredDetailOrder.rescue_new_store_name || '-'}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {expiredDetailOrder.rescue_new_store_address || ''}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Chip
                              size="small"
                              label={expiredDetailOrder.rescue_is_paid ? '已付款' : '待付款'}
                              color={expiredDetailOrder.rescue_is_paid ? 'success' : 'info'}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </>
                  )}

                  {expiredDetailOrder.pickup_expired_admin_notes && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="caption" color="text.secondary">處理備註</Typography>
                        <Typography variant="body2">{expiredDetailOrder.pickup_expired_admin_notes}</Typography>
                      </Box>
                    </>
                  )}
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExpiredDetailOpen(false)}>關閉</Button>
              {expiredDetailOrder && (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<Replay />}
                  onClick={() => { setExpiredDetailOpen(false); openRescueDialog(expiredDetailOrder); }}
                >
                  挽回：建立新訂單
                </Button>
              )}
              {expiredDetailOrder && (
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={() => { setExpiredDetailOpen(false); openResolveDialog(expiredDetailOrder); }}
                >
                  標記處理
                </Button>
              )}
            </DialogActions>
          </Dialog>

          {/* Resolve Dialog */}
          <Dialog
            open={expiredResolveDialogOpen}
            onClose={() => !expiredResolveSaving && setExpiredResolveDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>標記處理 — {expiredResolveTarget?.order_id}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {expiredResolveTarget && !isCodOrder(expiredResolveTarget.payment_method) && (
                  <Alert severity="warning">
                    此訂單為信用卡/LINE Pay 已付款，請於 ECPay/LINE Pay 後台執行退款後再標記為「已退款」。
                  </Alert>
                )}
                <FormControl fullWidth>
                  <InputLabel>處理方式</InputLabel>
                  <Select
                    value={expiredResolution}
                    label="處理方式"
                    onChange={(e) => setExpiredResolution(e.target.value)}
                  >
                    <MenuItem value=""><em>清除（標記為待處理）</em></MenuItem>
                    <MenuItem value="refunded">已退款</MenuItem>
                    <MenuItem value="reshipped">已重寄</MenuItem>
                    <MenuItem value="outlet">已加入 Outlet</MenuItem>
                    <MenuItem value="closed">結案 / 不處理</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="備註"
                  placeholder="例：客戶 LINE 回覆要重寄到家裡 / 已於 04/27 退款 NT$702"
                  value={expiredResolveNotes}
                  onChange={(e) => setExpiredResolveNotes(e.target.value)}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExpiredResolveDialogOpen(false)} disabled={expiredResolveSaving}>
                取消
              </Button>
              <Button
                variant="contained"
                onClick={handleResolveExpired}
                disabled={expiredResolveSaving}
                startIcon={expiredResolveSaving ? <CircularProgress size={16} /> : <CheckCircle />}
              >
                {expiredResolveSaving ? '儲存中...' : '儲存'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* 挽回 → 建立新訂單 Dialog */}
          <Dialog
            open={rescueDialogOpen}
            onClose={() => !rescueCreating && setRescueDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Replay color="success" />
                <span>挽回：建立新訂單 — 原訂單 {rescueTarget?.order_id}</span>
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              {rescueCreatedOrderId ? (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <Alert severity="success">
                    已建立新訂單 <strong>{rescueCreatedOrderId}</strong>，原訂單已標記為「已重寄」。
                  </Alert>
                  <Typography variant="body2" color="text.secondary">
                    下一步：到「出貨」頁面為 {rescueCreatedOrderId} 建立物流單並列印標籤。
                    退回的原包裹到貨後，照常在收貨頁面加入庫存或 Outlet。
                  </Typography>
                </Stack>
              ) : rescueTarget && (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <Alert severity={isCodOrder(rescueTarget.payment_method) ? 'info' : 'warning'}>
                    {isCodOrder(rescueTarget.payment_method) ? (
                      <>原訂單是 <strong>取貨付款</strong>且已自動取消（未收款），新訂單預設為<strong>貨到付款</strong>並收取金額。</>
                    ) : (
                      <>原訂單已用 {formatPaymentMethod(rescueTarget.payment_method)} 付款 NT$
                        {Number(rescueTarget.total_amount).toLocaleString()}，款項仍記在原訂單上，
                        新訂單預設<strong>不收費</strong>以免營收重複計算。若客戶需補差額，改選「已收款」並填金額。</>
                    )}
                  </Alert>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth size="small" label="收件人姓名"
                        value={rescueReceiverName}
                        onChange={(e) => setRescueReceiverName(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth size="small" label="收件人電話"
                        value={rescueReceiverPhone}
                        onChange={(e) => setRescueReceiverPhone(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>配送方式</InputLabel>
                        <Select
                          value={rescueDelivery}
                          label="配送方式"
                          onChange={(e) => {
                            const v = e.target.value as 'pickup' | 'shipping';
                            setRescueDelivery(v);
                            if (v !== 'pickup' && rescuePaymentMode === 'cod') setRescuePaymentMode('no_charge');
                          }}
                        >
                          <MenuItem value="pickup">超商取貨</MenuItem>
                          <MenuItem value="shipping">宅配</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {rescueDelivery === 'pickup' ? (
                      <>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>超商</InputLabel>
                            <Select
                              value={rescueLogisticsSubType}
                              label="超商"
                              onChange={(e) => setRescueLogisticsSubType(e.target.value)}
                            >
                              {RESCUE_LOGISTICS_OPTIONS.map(o => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth size="small" label="門市店號"
                            value={rescueStoreId}
                            onChange={(e) => setRescueStoreId(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            fullWidth size="small" label="門市名稱"
                            value={rescueStoreName}
                            onChange={(e) => setRescueStoreName(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth size="small" label="門市地址"
                            value={rescueStoreAddress}
                            onChange={(e) => setRescueStoreAddress(e.target.value)}
                          />
                        </Grid>
                      </>
                    ) : (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth size="small" label="收件地址"
                          value={rescueShippingAddress}
                          onChange={(e) => setRescueShippingAddress(e.target.value)}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>收款方式</InputLabel>
                        <Select
                          value={rescuePaymentMode}
                          label="收款方式"
                          onChange={(e) => {
                            const v = e.target.value as 'no_charge' | 'prepaid' | 'cod';
                            setRescuePaymentMode(v);
                            if (v !== 'no_charge' && rescueTarget) {
                              setRescueAmount(String(sumExpiredItems(rescueTarget.items || [], rescueItemKeys)));
                            }
                          }}
                        >
                          <MenuItem value="no_charge">不收費（原訂單已付款）</MenuItem>
                          <MenuItem value="prepaid">已收款</MenuItem>
                          <MenuItem value="cod" disabled={rescueDelivery !== 'pickup'}>貨到付款</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth size="small" type="number" label="金額 NT$"
                        value={rescuePaymentMode === 'no_charge' ? '0' : rescueAmount}
                        disabled={rescuePaymentMode === 'no_charge'}
                        onChange={(e) => setRescueAmount(e.target.value)}
                        helperText={`選取商品合計 NT$${sumExpiredItems(rescueTarget.items || [], rescueItemKeys).toLocaleString()}`}
                      />
                    </Grid>
                  </Grid>

                  <Divider />
                  <Typography variant="subtitle2">要重寄的商品（取消勾選＝這次不寄）</Typography>
                  {(rescueTarget.items || []).map((it, idx) => (
                    <Box key={it.id ?? idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.5 }}>
                      <Checkbox
                        size="small"
                        checked={rescueItemKeys.has(idx)}
                        onChange={() => toggleRescueItem(idx)}
                      />
                      <Avatar
                        src={it.image_url || undefined}
                        variant="rounded"
                        sx={{ width: 40, height: 40, bgcolor: 'grey.200' }}
                      >
                        {!it.image_url && <Inventory2 fontSize="small" />}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{it.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {it.variant_details || '-'} · x{it.quantity} · NT${expiredItemUnitPrice(it).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  ))}

                  <TextField
                    fullWidth size="small" label="備註（寫入新訂單）"
                    value={rescueNote}
                    onChange={(e) => setRescueNote(e.target.value)}
                  />
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRescueDialogOpen(false)} disabled={rescueCreating}>
                {rescueCreatedOrderId ? '關閉' : '取消'}
              </Button>
              {!rescueCreatedOrderId && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleCreateRescueOrder}
                  disabled={rescueCreating || rescueItemKeys.size === 0}
                  startIcon={rescueCreating ? <CircularProgress size={16} /> : <Replay />}
                >
                  {rescueCreating ? '建立中...' : '建立新訂單'}
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Storefront return → Outlet dialog */}
      <Dialog
        open={sfOutletDialog.open}
        onClose={() => !addingSfOutlet && setSfOutletDialog({ open: false, item: null, request: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>加入 Outlet</DialogTitle>
        <DialogContent>
          {sfOutletDialog.item && (() => {
            const item = sfOutletDialog.item;
            const originalPrice = parseFloat(item.price) || 0;
            const pct = parseFloat(sfOutletDiscountPercent);
            const discountPrice = Number.isFinite(pct) && pct > 0 && pct < 100 && originalPrice > 0
              ? Math.round(originalPrice * (1 - pct / 100))
              : 0;
            return (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  {(item.imageUrl || item.image_url) ? (
                    <Box
                      component="img"
                      src={item.imageUrl || item.image_url}
                      sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                    />
                  ) : (
                    <Box sx={{ width: 64, height: 64, bgcolor: 'grey.200', borderRadius: 1, flexShrink: 0 }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight="bold" noWrap>{item.productName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      款式: {item.variantTitle || 'Default'}
                    </Typography>
                    {originalPrice > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        原價: NT${originalPrice.toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <TextField
                  fullWidth
                  label="折扣百分比 (%)"
                  type="number"
                  value={sfOutletDiscountPercent}
                  onChange={(e) => setSfOutletDiscountPercent(e.target.value)}
                  helperText={
                    discountPrice > 0
                      ? `特價：NT$${discountPrice.toLocaleString()}（原價的 ${100 - pct}%）`
                      : '例如：40 表示打六折（原價的 60%）'
                  }
                  inputProps={{ min: 1, max: 99 }}
                />
                <TextField
                  fullWidth
                  label="數量"
                  type="number"
                  value={sfOutletQuantity}
                  onChange={(e) => setSfOutletQuantity(parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1 }}
                />
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSfOutletDialog({ open: false, item: null, request: null })}
            disabled={addingSfOutlet}
          >
            取消
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleAddSfReturnToOutlet}
            disabled={addingSfOutlet || !sfOutletDiscountPercent}
            startIcon={addingSfOutlet ? <CircularProgress size={16} /> : <AddShoppingCart />}
          >
            {addingSfOutlet ? '處理中...' : '確認加入'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Storefront return → Outlet batch dialog */}
      <Dialog
        open={sfBatchOutletDialog.open}
        onClose={() => !sfBatchAdding && setSfBatchOutletDialog({ open: false, request: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          批次加入 Outlet ({sfBatchSelectedKeys.size} 件商品)
        </DialogTitle>
        <DialogContent>
          {sfBatchOutletDialog.request && (() => {
            const items = Array.isArray(sfBatchOutletDialog.request.original_items)
              ? sfBatchOutletDialog.request.original_items
              : [];
            const pct = parseFloat(sfBatchDiscountPercent);
            return (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  統一設定折扣百分比，系統會根據每件商品的原價自動計算特價。
                </Typography>
                <TextField
                  fullWidth
                  label="折扣百分比 (%)"
                  type="number"
                  value={sfBatchDiscountPercent}
                  onChange={(e) => setSfBatchDiscountPercent(e.target.value)}
                  helperText="例如：40 表示打六折（原價的 60%）"
                  inputProps={{ min: 1, max: 99 }}
                />
                <Divider />
                <Typography variant="subtitle2">選取的商品：</Typography>
                {items.map((item: any, idx: number) => {
                  const originalPrice = parseFloat(item.price) || 0;
                  const discountPrice = Number.isFinite(pct) && pct > 0 && pct < 100 && originalPrice > 0
                    ? Math.round(originalPrice * (1 - pct / 100))
                    : 0;
                  const checked = sfBatchSelectedKeys.has(idx);
                  return (
                    <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.5 }}>
                      <Checkbox
                        size="small"
                        checked={checked}
                        disabled={originalPrice <= 0}
                        onChange={() => toggleSfBatchKey(idx)}
                      />
                      {(item.imageUrl || item.image_url) ? (
                        <Box
                          component="img"
                          src={item.imageUrl || item.image_url}
                          sx={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 0.5 }}
                        />
                      ) : (
                        <Box sx={{ width: 36, height: 36, bgcolor: 'grey.200', borderRadius: 0.5 }} />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{item.productName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          款式: {item.variantTitle || 'Default'} | 數量: {item.quantity}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {originalPrice > 0 ? (
                          <>
                            <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                              NT${originalPrice.toLocaleString()}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              → NT${discountPrice > 0 ? discountPrice.toLocaleString() : '?'}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="caption" color="error.main">無原價</Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSfBatchOutletDialog({ open: false, request: null })}
            disabled={sfBatchAdding}
          >
            取消
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleAddSfBatchToOutlet}
            disabled={sfBatchAdding || !sfBatchDiscountPercent || sfBatchSelectedKeys.size === 0}
            startIcon={sfBatchAdding ? <CircularProgress size={16} /> : <AddShoppingCart />}
          >
            {sfBatchAdding ? '處理中...' : `確認加入 ${sfBatchSelectedKeys.size} 件`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExchangeReturnManagementPage;